import { BACKEND_URL } from '../utils/config';
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import AdminReferralRewardsTable from '../components/admin/AdminReferralRewardsTable';
import { REFERRALS_ENABLED } from '../utils/featureFlags';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import {
  Users,
  FileCheck,
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Shield,
  AlertCircle,
  Loader2,
  ChevronRight,
  BarChart3,
  Gift,
  Target,
  UserCheck,
  FileText,
  RefreshCw,
  Star,
  Trash2,
  Settings,
  Briefcase,
  Download,
  Mail,
  Phone,
  Search,
  X,
  BadgeCheck,
  MapPin,
  Calendar,
  Hash,
  CheckSquare,
  PieChart,
  MessageSquare,
  Send,
  Activity,
  Flag,
  Bell,
  AlertTriangle,
  UserCog,
  Ban,
  UserX,
  ExternalLink,
  Zap,
} from 'lucide-react';
import notificationSound from '../utils/notificationSound';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import ContentManager from '../components/admin/ContentManager';
import AdminDisputesSection from '../components/AdminDisputesSection';
import AdminCommentsSection from '../components/AdminCommentsSection';
import AdminPayoutManagement from '../components/admin/AdminPayoutManagement';
import AdminInvoicesSection from '../components/billing/AdminInvoicesSection';
import { Wallet } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pendingKYC, setPendingKYC] = useState([]);
  const [pendingCampaigns, setPendingCampaigns] = useState([]);
  const [pendingStories, setPendingStories] = useState([]);
  const [deletedCampaigns, setDeletedCampaigns] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [allCampaignsLoading, setAllCampaignsLoading] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState('all'); // 'all', 'active', 'pending_verification', 'deleted'
  const [enquiries, setEnquiries] = useState([]);
  const [enquiriesLoading, setEnquiriesLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState({});
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  
  // New admin module states
  const [teamPages, setTeamPages] = useState([]);
  const [teamPagesLoading, setTeamPagesLoading] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '', type: 'info' });
  const [deleteAnnouncementId, setDeleteAnnouncementId] = useState(null);
  const [showDeleteAnnouncementConfirm, setShowDeleteAnnouncementConfirm] = useState(false);
  
  // Enquiry Detail Modal
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  
  // Background Jobs Monitoring (MON-005)
  const [activeJobs, setActiveJobs] = useState([]);
  const [jobHistory, setJobHistory] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  
  // Error Summary Monitoring (MON-006)
  const [errorSummary, setErrorSummary] = useState(null);
  const [errorSummaryLoading, setErrorSummaryLoading] = useState(false);
  
  // KYC Rejection Modal
  const [showKYCRejectModal, setShowKYCRejectModal] = useState(false);
  const [kycRejectUserId, setKycRejectUserId] = useState(null);
  const [kycRejectReason, setKycRejectReason] = useState('');
  
  // All Campaigns - Enhanced filters and pagination
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');
  const [campaignSortBy, setCampaignSortBy] = useState('newest');
  const [campaignCurrentPage, setCampaignCurrentPage] = useState(1);
  const [campaignItemsPerPage, setCampaignItemsPerPage] = useState(20);
  
  // Get initial tab from URL params or default based on role
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    // Block direct ?tab=referral-rewards when feature is disabled
    if (!REFERRALS_ENABLED && tabParam === 'referral-rewards') {
      return user?.role === 'reviewer' ? 'kyc' : 'overview';
    }
    if (tabParam && ['overview', 'kyc', 'campaigns', 'all-campaigns', 'stories', 'content', 'applications', 'deleted', 'enquiries', 'team-pages', 'activity', 'audit-logs', 'reports', 'announcements', 'analytics', 'referral-rewards', 'users-management', 'disputes', 'comments', 'payouts', 'invoices', 'background-jobs', 'error-summary'].includes(tabParam)) {
      return tabParam;
    }
    return user?.role === 'reviewer' ? 'kyc' : 'overview';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [jobApplications, setJobApplications] = useState([]);
  
  // Member ID Search
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Multi-select for bulk actions
  const [selectedDeletedCampaigns, setSelectedDeletedCampaigns] = useState([]);
  const [selectedPendingCampaigns, setSelectedPendingCampaigns] = useState([]);
  const [selectedEnquiries, setSelectedEnquiries] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Detail modals
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showCampaignsModal, setShowCampaignsModal] = useState(false);
  const [showDonationsModal, setShowDonationsModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [recentDonations, setRecentDonations] = useState([]);
  const [donationsLoading, setDonationsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState({});

  // Export function - handles authenticated CSV download
  const handleExport = async (type) => {
    setExportLoading(prev => ({ ...prev, [type]: true }));
    try {
      let endpoint = '';
      let filename = '';
      
      if (type === 'donations') {
        endpoint = '/admin/export/donations?format=csv';
        filename = 'donations_export.csv';
      } else if (type === 'campaigns') {
        endpoint = '/admin/export/campaigns?format=csv';
        filename = 'campaigns_export.csv';
      } else if (type === 'users') {
        endpoint = '/admin/export/users?format=csv';
        filename = 'users_export.csv';
      }
      
      const response = await apiClient.get(endpoint, {
        responseType: 'blob'
      });
      
      // Check if response is actually a blob/file
      if (response.data instanceof Blob && response.data.size > 0) {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        // Response might be JSON with no data
        alert('No data available to export');
      }
    } catch (error) {
      console.error('Export failed:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to export this data');
      } else {
        alert('Failed to export data. Please try again.');
      }
    } finally {
      setExportLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Update active tab when URL params change
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'kyc', 'campaigns', 'stories', 'content', 'applications', 'deleted', 'enquiries', 'disputes', 'comments'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Update active tab when user loads (for reviewers)
  useEffect(() => {
    if (user?.role === 'reviewer' && activeTab === 'overview') {
      setActiveTab('kyc');
    }
  }, [user?.role]);

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login?redirect=admin');
      return;
    }
    fetchAdminData();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchAdminData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // FAST: Load critical stats first - show UI immediately
      const statsRes = await apiClient.get('/admin/stats');
      setStats(statsRes.data);
      setLoading(false); // UI renders NOW
      
      // BACKGROUND: Load secondary data (non-blocking)
      Promise.all([
        apiClient.get('/admin/kyc/pending'),
        apiClient.get('/admin/campaigns/pending'),
        apiClient.get('/admin/activity?limit=5'),
        apiClient.get('/success-stories/pending').catch(() => ({ data: { pending_stories: [] } })),
        apiClient.get('/careers/applications').catch(() => ({ data: [] })),
        apiClient.get('/platform/campaigns/deleted').catch(() => ({ data: { deleted_campaigns: [] } })),
        apiClient.get('/enquiries/').catch(() => ({ data: { enquiries: [] } })),
      ]).then(([kycRes, campaignsRes, activityRes, storiesRes, applicationsRes, deletedRes, enquiriesRes]) => {
        setPendingKYC(kycRes.data.pending_kyc || []);
        setPendingCampaigns(campaignsRes.data.pending_campaigns || []);
        setRecentActivity(activityRes.data);
        setPendingStories(storiesRes.data.pending_stories || []);
        setJobApplications(applicationsRes.data || []);
        setDeletedCampaigns(deletedRes.data.deleted_campaigns || []);
        setEnquiries(enquiriesRes.data.enquiries || []);
      }).catch(console.error);
      
    } catch (err) {
      setLoading(false);
      if (err.response?.status === 403) {
        setError('You do not have admin access. Please contact support.');
      } else {
        setError('Failed to load admin data');
      }
      console.error('Admin data error:', err);
    }
  };

  // Poll for new enquiries and play sound notification
  const [lastEnquiryCount, setLastEnquiryCount] = useState(0);
  useEffect(() => {
    const newEnquiryCount = enquiries.filter(e => e.status === 'new').length;
    if (newEnquiryCount > lastEnquiryCount && lastEnquiryCount >= 0) {
      notificationSound.playEnquiryAlert();
    }
    setLastEnquiryCount(newEnquiryCount);
  }, [enquiries]);

  // Fetch Team Pages for admin
  const fetchTeamPages = async () => {
    setTeamPagesLoading(true);
    try {
      const response = await apiClient.get('/team-pages/admin/all');
      setTeamPages(response.data.team_pages || []);
    } catch (err) {
      console.error('Failed to fetch team pages:', err);
    } finally {
      setTeamPagesLoading(false);
    }
  };

  // Fetch All Campaigns with filter
  const fetchAllCampaigns = async (status = 'all') => {
    setAllCampaignsLoading(true);
    try {
      const url = status === 'all' ? '/admin/campaigns?limit=200' : `/admin/campaigns?status=${status}&limit=200`;
      const response = await apiClient.get(url);
      setAllCampaigns(response.data.campaigns || []);
    } catch (err) {
      console.error('Failed to fetch all campaigns:', err);
      setAllCampaigns([]);
    } finally {
      setAllCampaignsLoading(false);
    }
  };
  
  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'all-campaigns' && allCampaigns.length === 0 && !allCampaignsLoading) {
      fetchAllCampaigns(campaignFilter);
    }
    if (activeTab === 'activity' && activityLogs.length === 0 && !activityLogsLoading) {
      fetchActivityLogs();
    }
    if (activeTab === 'audit-logs' && auditLogs.length === 0 && !auditLogsLoading) {
      fetchAuditLogs();
    }
  }, [activeTab]);

  // Fetch Activity Logs
  const fetchActivityLogs = async () => {
    setActivityLogsLoading(true);
    try {
      const response = await apiClient.get('/admin/activity?limit=100');
      setActivityLogs(response.data.activities || []);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
    } finally {
      setActivityLogsLoading(false);
    }
  };

  // Fetch Audit Logs (Admin Actions)
  const fetchAuditLogs = async () => {
    setAuditLogsLoading(true);
    try {
      const response = await apiClient.get('/platform/audit-logs?limit=100');
      setAuditLogs(response.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setAuditLogsLoading(false);
    }
  };

  // Fetch Background Jobs (MON-005)
  const fetchBackgroundJobs = async () => {
    setJobsLoading(true);
    try {
      const [activeRes, historyRes] = await Promise.all([
        apiClient.get('/monitoring/jobs/active'),
        apiClient.get('/monitoring/jobs/history?limit=50')
      ]);
      // Merge active + recently completed so the panel is never empty
      const active = activeRes.data.active_jobs || [];
      const recent = activeRes.data.recent_completed || [];
      // Mark recent jobs so UI can render them with proper styling
      const recentTagged = recent.map(j => ({ ...j, _recent: true }));
      setActiveJobs([...active, ...recentTagged]);
      setJobHistory(historyRes.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch background jobs:', err);
    } finally {
      setJobsLoading(false);
    }
  };

  // Fetch Error Summary (MON-006)
  const fetchErrorSummary = async () => {
    setErrorSummaryLoading(true);
    try {
      const response = await apiClient.get('/monitoring/errors/summary');
      setErrorSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch error summary:', err);
    } finally {
      setErrorSummaryLoading(false);
    }
  };

  // Fetch Reports
  const fetchReports = async () => {
    setReportsLoading(true);
    try {
      const response = await apiClient.get('/admin/reports');
      setReports(response.data.reports || []);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setReportsLoading(false);
    }
  };

  // Fetch Announcements
  const fetchAnnouncements = async () => {
    setAnnouncementsLoading(true);
    try {
      const response = await apiClient.get('/admin/announcements');
      setAnnouncements(response.data.announcements || []);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  // Create Announcement
  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) return;
    try {
      await apiClient.post('/admin/announcements', newAnnouncement);
      setNewAnnouncement({ title: '', message: '', type: 'info' });
      fetchAnnouncements();
      alert('Announcement created successfully!');
    } catch (err) {
      console.error('Failed to create announcement:', err);
      alert('Failed to create announcement: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  // Delete Announcement - show confirmation first
  const handleDeleteAnnouncementClick = (id) => {
    setDeleteAnnouncementId(id);
    setShowDeleteAnnouncementConfirm(true);
  };
  
  // Confirm Delete Announcement
  const handleConfirmDeleteAnnouncement = async () => {
    if (!deleteAnnouncementId) return;
    try {
      await apiClient.delete(`/admin/announcements/${deleteAnnouncementId}`);
      fetchAnnouncements();
      alert('Announcement deleted successfully!');
    } catch (err) {
      console.error('Failed to delete announcement:', err);
      alert('Failed to delete announcement');
    } finally {
      setShowDeleteAnnouncementConfirm(false);
      setDeleteAnnouncementId(null);
    }
  };
  
  // Cancel Delete Announcement
  const handleCancelDeleteAnnouncement = () => {
    setShowDeleteAnnouncementConfirm(false);
    setDeleteAnnouncementId(null);
  };

  // User Management states
  const [managedUsers, setManagedUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userActionLoading, setUserActionLoading] = useState({});
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');

  // Fetch users for management
  const fetchManagedUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await apiClient.get('/admin/users', {
        params: { 
          search: userSearch || undefined,
          role: userRoleFilter !== 'all' ? userRoleFilter : undefined,
          limit: 50
        }
      });
      setManagedUsers(response.data.users || response.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  // Change user role
  const handleChangeUserRole = async (userId, newRole) => {
    setUserActionLoading(prev => ({ ...prev, [`role-${userId}`]: true }));
    try {
      await apiClient.put(`/admin/users/${userId}/role`, { role: newRole });
      fetchManagedUsers();
      alert(`User role updated to ${newRole}`);
    } catch (err) {
      console.error('Failed to change role:', err);
      alert('Failed to change user role: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setUserActionLoading(prev => ({ ...prev, [`role-${userId}`]: false }));
    }
  };

  // Block user
  const handleBlockUser = async (userId) => {
    if (!window.confirm('Are you sure you want to block this user? They will not be able to access their account.')) return;
    setUserActionLoading(prev => ({ ...prev, [`block-${userId}`]: true }));
    try {
      await apiClient.post(`/admin/users/${userId}/block`);
      fetchManagedUsers();
      alert('User blocked successfully');
    } catch (err) {
      console.error('Failed to block user:', err);
      alert('Failed to block user: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setUserActionLoading(prev => ({ ...prev, [`block-${userId}`]: false }));
    }
  };

  // Unblock user
  const handleUnblockUser = async (userId) => {
    setUserActionLoading(prev => ({ ...prev, [`unblock-${userId}`]: true }));
    try {
      await apiClient.post(`/admin/users/${userId}/unblock`);
      fetchManagedUsers();
      alert('User unblocked successfully');
    } catch (err) {
      console.error('Failed to unblock user:', err);
      alert('Failed to unblock user: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setUserActionLoading(prev => ({ ...prev, [`unblock-${userId}`]: false }));
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    setUserActionLoading(prev => ({ ...prev, [`delete-${userId}`]: true }));
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      fetchManagedUsers();
      alert('User deleted successfully');
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Failed to delete user: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setUserActionLoading(prev => ({ ...prev, [`delete-${userId}`]: false }));
    }
  };

  // Resolve Report
  const handleResolveReport = async (id) => {
    try {
      await apiClient.post(`/admin/reports/${id}/resolve`);
      fetchReports();
      alert('Report marked as resolved');
    } catch (err) {
      console.error('Failed to resolve report:', err);
      alert('Failed to resolve report');
    }
  };

  // Disable Team Page (admin)
  const handleDisableTeamPage = async (id, reason) => {
    setActionLoading(prev => ({ ...prev, [`team-${id}`]: true }));
    try {
      await apiClient.post(`/team-pages/${id}/disable`, { reason: reason || 'Disabled by admin' });
      fetchTeamPages();
    } catch (err) {
      console.error('Failed to disable team page:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [`team-${id}`]: false }));
    }
  };

  // Delete Team Page (admin)
  const handleDeleteTeamPage = async (id, title) => {
    if (!window.confirm(`Are you sure you want to permanently delete the team page "${title}"? This action cannot be undone.`)) {
      return;
    }
    setActionLoading(prev => ({ ...prev, [`team-delete-${id}`]: true }));
    try {
      await apiClient.post(`/team-pages/${id}/remove`, { reason: 'Removed by admin', permanent: true });
      fetchTeamPages();
    } catch (err) {
      console.error('Failed to delete team page:', err);
      alert('Failed to delete team page: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`team-delete-${id}`]: false }));
    }
  };

  // Export Team Pages (admin)
  const handleExportTeamPages = () => {
    if (teamPages.length === 0) {
      alert('No team pages to export');
      return;
    }
    
    const headers = ['ID', 'Title', 'Creator', 'Campaign', 'Goal', 'Raised', 'Donors', 'Status', 'Created At'];
    const rows = teamPages.map(page => [
      page.id,
      page.title,
      page.creator_name || 'Unknown',
      page.campaign_title || page.parent_campaign_id || 'Unknown',
      page.goal || 0,
      page.raised || page.amount_raised || 0,
      page.donor_count || 0,
      page.status,
      page.created_at || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `team-pages-export-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Periodically check for new enquiries
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const pollEnquiries = async () => {
      try {
        const response = await apiClient.get('/enquiries/');
        const newEnquiries = response.data.enquiries || [];
        const currentNewCount = enquiries.filter(e => e.status === 'new').length;
        const newNewCount = newEnquiries.filter(e => e.status === 'new').length;
        
        if (newNewCount > currentNewCount) {
          notificationSound.playEnquiryAlert();
        }
        setEnquiries(newEnquiries);
      } catch (err) {
        // Silently fail
      }
    };
    
    const interval = setInterval(pollEnquiries, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [isAuthenticated, enquiries]);

  // Search user by Member ID
  const searchUserByMemberId = async () => {
    if (!memberSearch.trim()) return;
    
    setSearchLoading(true);
    setSearchError('');
    setSearchResult(null);
    
    try {
      const response = await apiClient.get(`/admin/users/member/${memberSearch.trim()}`);
      setSearchResult(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setSearchError(`No user found with Member ID: ${memberSearch}`);
      } else {
        setSearchError('Search failed. Please try again.');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const clearSearch = () => {
    setMemberSearch('');
    setSearchResult(null);
    setSearchError('');
  };

  const handleKYCDecision = async (userId, decision, reason = '') => {
    setActionLoading(prev => ({ ...prev, [`kyc-${userId}`]: true }));
    try {
      await apiClient.post(`/admin/kyc/${userId}/decision`, {
        decision,
        reason: reason || undefined,
      });
      // Refresh data
      fetchAdminData();
    } catch (err) {
      console.error('KYC decision error:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [`kyc-${userId}`]: false }));
    }
  };

  const handleCampaignDecision = async (campaignId, decision, reason = '') => {
    setActionLoading(prev => ({ ...prev, [`campaign-${campaignId}`]: true }));
    try {
      await apiClient.post(`/admin/campaigns/${campaignId}/decision`, {
        decision,
        reason: reason || undefined,
      });
      // Refresh data
      fetchAdminData();
    } catch (err) {
      console.error('Campaign decision error:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [`campaign-${campaignId}`]: false }));
    }
  };

  // Handle restoring a deleted campaign
  const handleRestoreCampaign = async (campaignId) => {
    setActionLoading(prev => ({ ...prev, [`restore-${campaignId}`]: true }));
    try {
      await apiClient.post(`/platform/campaigns/${campaignId}/action`, {
        action: 'restore',
        reason: 'Restored by admin',
      });
      // Remove from deleted list
      setDeletedCampaigns(prev => prev.filter(c => c.id !== campaignId));
      // Refresh data to show in pending campaigns
      fetchAdminData();
    } catch (err) {
      console.error('Restore campaign error:', err);
      alert('Failed to restore campaign: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`restore-${campaignId}`]: false }));
    }
  };

  // Bulk restore deleted campaigns
  const handleBulkRestore = async () => {
    if (selectedDeletedCampaigns.length === 0) return;
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedDeletedCampaigns.map(campaignId =>
          apiClient.post(`/platform/campaigns/${campaignId}/action`, {
            action: 'restore',
            reason: 'Bulk restored by admin',
          })
        )
      );
      // Remove restored campaigns from list
      setDeletedCampaigns(prev => prev.filter(c => !selectedDeletedCampaigns.includes(c.id)));
      setSelectedDeletedCampaigns([]);
      fetchAdminData();
    } catch (err) {
      console.error('Bulk restore error:', err);
      alert('Some campaigns failed to restore. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk approve pending campaigns
  const handleBulkApprove = async () => {
    if (selectedPendingCampaigns.length === 0) return;
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedPendingCampaigns.map(campaignId =>
          apiClient.post(`/admin/campaigns/${campaignId}/decision`, {
            decision: 'approved',
          })
        )
      );
      // Remove approved campaigns from pending list
      setPendingCampaigns(prev => prev.filter(c => !selectedPendingCampaigns.includes(c.id)));
      setSelectedPendingCampaigns([]);
      fetchAdminData();
    } catch (err) {
      console.error('Bulk approve error:', err);
      alert('Some campaigns failed to approve. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk reject pending campaigns
  const handleBulkReject = async () => {
    if (selectedPendingCampaigns.length === 0) return;
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedPendingCampaigns.map(campaignId =>
          apiClient.post(`/admin/campaigns/${campaignId}/decision`, {
            decision: 'rejected',
            reason: 'Bulk rejected - does not meet guidelines',
          })
        )
      );
      // Remove rejected campaigns from pending list
      setPendingCampaigns(prev => prev.filter(c => !selectedPendingCampaigns.includes(c.id)));
      setSelectedPendingCampaigns([]);
      fetchAdminData();
    } catch (err) {
      console.error('Bulk reject error:', err);
      alert('Some campaigns failed to reject. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Admin delete campaign from All Campaigns tab
  const handleAdminDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    setActionLoading(prev => ({ ...prev, [`delete-${campaignId}`]: true }));
    try {
      await apiClient.post(`/platform/campaigns/${campaignId}/action`, {
        action: 'delete',
        reason: 'Deleted by admin',
      });
      // Refresh the all campaigns list
      fetchAllCampaigns(campaignFilter);
    } catch (err) {
      console.error('Admin delete campaign error:', err);
      alert('Failed to delete campaign: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete-${campaignId}`]: false }));
    }
  };

  // Admin restore campaign from All Campaigns tab
  const handleAdminRestoreCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to restore this campaign?')) return;
    setActionLoading(prev => ({ ...prev, [`restore-${campaignId}`]: true }));
    try {
      await apiClient.post(`/platform/campaigns/${campaignId}/action`, {
        action: 'restore',
        reason: 'Restored by admin',
      });
      // Refresh the all campaigns list
      fetchAllCampaigns(campaignFilter);
    } catch (err) {
      console.error('Admin restore campaign error:', err);
      alert('Failed to restore campaign: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`restore-${campaignId}`]: false }));
    }
  };

  // Toggle select all deleted campaigns
  const toggleSelectAllDeleted = () => {
    if (selectedDeletedCampaigns.length === deletedCampaigns.length) {
      setSelectedDeletedCampaigns([]);
    } else {
      setSelectedDeletedCampaigns(deletedCampaigns.map(c => c.id));
    }
  };

  // Toggle select all pending campaigns
  const toggleSelectAllPending = () => {
    if (selectedPendingCampaigns.length === pendingCampaigns.length) {
      setSelectedPendingCampaigns([]);
    } else {
      setSelectedPendingCampaigns(pendingCampaigns.map(c => c.id));
    }
  };

  // Toggle individual deleted campaign selection
  const toggleDeletedCampaignSelection = (campaignId) => {
    setSelectedDeletedCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  // Toggle individual pending campaign selection
  const togglePendingCampaignSelection = (campaignId) => {
    setSelectedPendingCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  // Fetch recent donations for modal
  const fetchRecentDonations = async () => {
    setDonationsLoading(true);
    try {
      const response = await apiClient.get('/donations/recent?limit=50');
      // Handle both array response and object with donations property
      const donations = Array.isArray(response.data) ? response.data : (response.data.donations || []);
      setRecentDonations(donations);
    } catch (err) {
      console.error('Failed to fetch donations:', err);
      setRecentDonations([]);
    } finally {
      setDonationsLoading(false);
    }
  };

  // Open donations modal
  const openDonationsModal = () => {
    setShowDonationsModal(true);
    fetchRecentDonations();
  };

  // Fetch signed URL for viewing KYC documents
  const viewDocument = async (docUrl) => {
    try {
      // If it's already a full URL (legacy), open directly
      if (docUrl.startsWith('http')) {
        window.open(docUrl, '_blank');
        return;
      }
      
      // Otherwise, get a signed URL from R2
      const response = await apiClient.get(`/r2/signed-url/${encodeURIComponent(docUrl)}`);
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get signed URL:', err);
      alert('Unable to view document. Please try again.');
    }
  };

  // Handle success story decision
  const handleStoryDecision = async (storyId, decision, reason = '') => {
    setActionLoading(prev => ({ ...prev, [`story-${storyId}`]: true }));
    try {
      await apiClient.post(`/success-stories/${storyId}/review`, {
        decision,
        reason: reason || undefined,
      });
      fetchAdminData();
    } catch (err) {
      console.error('Story decision error:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [`story-${storyId}`]: false }));
    }
  };

  // Delete success story
  const handleDeleteStory = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;
    setActionLoading(prev => ({ ...prev, [`story-${storyId}`]: true }));
    try {
      await apiClient.delete(`/success-stories/${storyId}`);
      fetchAdminData();
    } catch (err) {
      console.error('Delete story error:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [`story-${storyId}`]: false }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-8 w-full">
          <Alert className="bg-red-500/10 border-red-500/50">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <AlertDescription className="text-red-400">{error}</AlertDescription>
          </Alert>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header with Role Badge */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                  <Shield className="w-8 h-8 text-emerald-400" />
                  Admin Dashboard
                </h1>
                {/* Role Badge */}
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  user?.role === 'super_admin' ? 'bg-red-500/20 text-red-400' :
                  user?.role === 'platform_admin' ? 'bg-purple-500/20 text-purple-400' :
                  user?.role === 'reviewer' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-amber-500/20 text-amber-400'
                }`}>
                  {user?.role === 'super_admin' ? '👑 Super Admin' :
                   user?.role === 'platform_admin' ? '⚙️ Platform Admin' :
                   user?.role === 'reviewer' ? '📋 Reviewer' : '🛡️ Admin'}
                </span>
              </div>
              <p className="text-muted-foreground">
                {user?.role === 'super_admin' ? 'Full platform control - Manage admins, users, and all settings' :
                 user?.role === 'platform_admin' ? 'Manage users, campaigns, analytics, and content' :
                 user?.role === 'reviewer' ? 'Review and verify KYC submissions & campaigns' :
                 'Manage verifications and moderate content'}
              </p>
            </div>
            <Button onClick={fetchAdminData} variant="outline" className="border-border">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Member ID Search */}
          <Card className="bg-card/50 border-border mb-8">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search by Member ID (e.g., 639377)"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchUserByMemberId()}
                    className="pl-10 bg-secondary border-border text-foreground placeholder:text-muted-foreground"
                  />
                  {memberSearch && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Button 
                  onClick={searchUserByMemberId}
                  disabled={searchLoading || !memberSearch.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Search User
                </Button>
              </div>
              
              {/* Search Error */}
              {searchError && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {searchError}
                </div>
              )}
              
              {/* Search Result */}
              {searchResult && (
                <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                  <div className="flex items-start gap-4">
                    <img
                      src={searchResult.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(searchResult.name)}&size=64`}
                      alt={searchResult.name}
                      className="w-16 h-16 rounded-full border-2 border-emerald-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-foreground">{searchResult.name}</h3>
                        {searchResult.verified && <BadgeCheck className="w-5 h-5 text-emerald-400" />}
                        <Badge className="bg-emerald-500/20 text-emerald-400 font-mono text-xs">
                          ID: {searchResult.member_id}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-1 text-foreground/80">
                          <Mail className="w-3 h-3 text-muted-foreground" />
                          {searchResult.email}
                        </div>
                        {searchResult.phone && (
                          <div className="flex items-center gap-1 text-foreground/80">
                            <Phone className="w-3 h-3 text-muted-foreground" />
                            {searchResult.phone}
                          </div>
                        )}
                        {searchResult.location && (
                          <div className="flex items-center gap-1 text-foreground/80">
                            <MapPin className="w-3 h-3 text-muted-foreground" />
                            {searchResult.location}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-foreground/80">
                          <Calendar className="w-3 h-3 text-muted-foreground" />
                          Joined {new Date(searchResult.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3">
                        <Badge className={`${
                          searchResult.kyc_status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                          searchResult.kyc_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          searchResult.kyc_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                          'bg-slate-500/20 text-muted-foreground'
                        }`}>
                          KYC: {searchResult.kyc_status || 'Not Submitted'}
                        </Badge>
                        <Badge className={searchResult.email_verified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-muted-foreground'}>
                          Email: {searchResult.email_verified ? 'Verified' : 'Not Verified'}
                        </Badge>
                        <Badge className={searchResult.phone_verified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-muted-foreground'}>
                          Phone: {searchResult.phone_verified ? 'Verified' : 'Not Verified'}
                        </Badge>
                        <span className="text-muted-foreground text-xs ml-auto">
                          Role: <span className="text-foreground capitalize">{searchResult.role}</span>
                        </span>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="border-border text-foreground/80 hover:bg-secondary"
                          onClick={() => navigate(`/profile/${searchResult.id}`)}
                        >
                          <Eye className="w-3 h-3 mr-1" /> View Profile
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                          onClick={() => navigate(`/messages?user=${searchResult.id}`)}
                        >
                          <Mail className="w-3 h-3 mr-1" /> Message User
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats Cards - Clickable with Modals */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card 
              className="bg-card/50 border-border cursor-pointer hover:border-emerald-500/50 transition-all"
              onClick={() => setShowUsersModal(true)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Users</p>
                    <p className="text-3xl font-bold text-foreground">{stats?.users?.total || 0}</p>
                    <p className="text-xs text-emerald-400 mt-1">
                      {stats?.users?.verified || 0} verified
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-500/20 rounded-xl">
                    <Users className="w-6 h-6 text-emerald-400" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Click to view user details →</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-card/50 border-border cursor-pointer hover:border-emerald-500/50 transition-all"
              onClick={() => setShowCampaignsModal(true)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Campaigns</p>
                    <p className="text-3xl font-bold text-foreground">{stats?.campaigns?.total || 0}</p>
                    <p className="text-xs text-emerald-400 mt-1">
                      {stats?.campaigns?.active || 0} active
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-500/20 rounded-xl">
                    <Target className="w-6 h-6 text-emerald-400" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Click to view campaign details →</p>
              </CardContent>
            </Card>

            {/* Financial Stats - Hide for Reviewers */}
            {user?.role !== 'reviewer' ? (
              <Card 
                className="bg-card/50 border-border cursor-pointer hover:border-teal-500/50 transition-all"
                onClick={openDonationsModal}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-muted-foreground text-sm">Total Raised</p>
                      <p className="text-3xl font-bold text-foreground">
                        {formatCurrency(stats?.donations?.amount_raised || 0)}
                      </p>
                      <p className="text-xs text-teal-400 mt-1">
                        {stats?.donations?.total || 0} donations
                      </p>
                    </div>
                    <div className="p-3 bg-teal-500/20 rounded-xl">
                      <DollarSign className="w-6 h-6 text-teal-400" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Click to view donation details →</p>
                </CardContent>
              </Card>
            ) : (
              <Card 
                className="bg-card/50 border-border cursor-pointer hover:border-yellow-500/50 transition-all"
                onClick={() => setShowPendingModal(true)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-muted-foreground text-sm">KYC Pending</p>
                      <p className="text-3xl font-bold text-foreground">{stats?.kyc?.pending || 0}</p>
                      <p className="text-xs text-yellow-400 mt-1">
                        Awaiting your review
                      </p>
                    </div>
                    <div className="p-3 bg-yellow-500/20 rounded-xl">
                      <UserCheck className="w-6 h-6 text-yellow-400" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Click to view pending details →</p>
                </CardContent>
              </Card>
            )}

            <Card 
              className="bg-card/50 border-border cursor-pointer hover:border-yellow-500/50 transition-all"
              onClick={() => setShowPendingModal(true)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Pending Reviews</p>
                    <p className="text-3xl font-bold text-foreground">
                      {(stats?.kyc?.pending || 0) + (stats?.campaigns?.pending || 0)}
                    </p>
                    <p className="text-xs text-yellow-400 mt-1">
                      {stats?.kyc?.pending || 0} KYC, {stats?.campaigns?.pending || 0} campaigns
                    </p>
                  </div>
                  <div className="p-3 bg-yellow-500/20 rounded-xl">
                    <Clock className="w-6 h-6 text-yellow-400" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Click to view pending details →</p>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="overflow-x-auto pb-2 mb-4">
              <TabsList className="bg-card border border-border inline-flex min-w-max gap-1 p-1">
              {/* Overview - Only for non-reviewers */}
              {user?.role !== 'reviewer' && (
                <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-500">
                  <BarChart3 className="w-4 h-4 mr-2" /> Overview
                </TabsTrigger>
              )}
              <TabsTrigger value="kyc" className="data-[state=active]:bg-emerald-500">
                <FileCheck className="w-4 h-4 mr-2" /> KYC Reviews
                {pendingKYC.length > 0 && (
                  <Badge className="ml-2 bg-yellow-500 text-black">{pendingKYC.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="campaigns" className="data-[state=active]:bg-emerald-500">
                <FileText className="w-4 h-4 mr-2" /> Campaign Reviews
                {pendingCampaigns.length > 0 && (
                  <Badge className="ml-2 bg-yellow-500 text-black">{pendingCampaigns.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="all-campaigns" className="data-[state=active]:bg-blue-500" onClick={() => fetchAllCampaigns(campaignFilter)}>
                <Target className="w-4 h-4 mr-2" /> All Campaigns
              </TabsTrigger>
              <TabsTrigger value="stories" className="data-[state=active]:bg-emerald-500">
                <Star className="w-4 h-4 mr-2" /> Success Stories
                {pendingStories.length > 0 && (
                  <Badge className="ml-2 bg-amber-500 text-black">{pendingStories.length}</Badge>
                )}
              </TabsTrigger>
              {/* Content & Applications - Only for Platform Admin and above */}
              {['platform_admin', 'super_admin', 'admin'].includes(user?.role) && (
                <>
                  <TabsTrigger value="content" className="data-[state=active]:bg-emerald-500">
                    <Settings className="w-4 h-4 mr-2" /> Content
                  </TabsTrigger>
                  <TabsTrigger value="applications" className="data-[state=active]:bg-emerald-500">
                    <Briefcase className="w-4 h-4 mr-2" /> Job Applications
                    {jobApplications.filter(a => a.status === 'pending').length > 0 && (
                      <Badge className="ml-2 bg-amber-500 text-black">
                        {jobApplications.filter(a => a.status === 'pending').length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="deleted" className="data-[state=active]:bg-red-500">
                    <Trash2 className="w-4 h-4 mr-2" /> Deleted Campaigns
                    {deletedCampaigns.length > 0 && (
                      <Badge className="ml-2 bg-red-500 text-foreground">{deletedCampaigns.length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="enquiries" className="data-[state=active]:bg-amber-500">
                    <MessageSquare className="w-4 h-4 mr-2" /> Enquiries
                    {enquiries.filter(e => e.status === 'new').length > 0 && (
                      <Badge className="ml-2 bg-amber-500 text-foreground animate-pulse">{enquiries.filter(e => e.status === 'new').length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="team-pages" className="data-[state=active]:bg-teal-500" onClick={fetchTeamPages}>
                    <Users className="w-4 h-4 mr-2" /> Team Pages
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="data-[state=active]:bg-blue-500" onClick={fetchActivityLogs}>
                    <Activity className="w-4 h-4 mr-2" /> Activity Logs
                  </TabsTrigger>
                  <TabsTrigger value="reports" className="data-[state=active]:bg-red-500" onClick={fetchReports}>
                    <Flag className="w-4 h-4 mr-2" /> Reports
                    {reports.filter(r => r.status === 'open').length > 0 && (
                      <Badge className="ml-2 bg-red-500 text-foreground">{reports.filter(r => r.status === 'open').length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="announcements" className="data-[state=active]:bg-purple-500" onClick={fetchAnnouncements}>
                    <Bell className="w-4 h-4 mr-2" /> Announcements
                  </TabsTrigger>
                  <TabsTrigger value="analytics" className="data-[state=active]:bg-cyan-500">
                    <TrendingUp className="w-4 h-4 mr-2" /> Analytics
                  </TabsTrigger>
                  {REFERRALS_ENABLED && (
                    <TabsTrigger value="referral-rewards" className="data-[state=active]:bg-amber-500">
                      <Gift className="w-4 h-4 mr-2" /> Referral Rewards
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="users-management" className="data-[state=active]:bg-indigo-500">
                    <UserCog className="w-4 h-4 mr-2" /> User Management
                  </TabsTrigger>
                  <TabsTrigger value="disputes" className="data-[state=active]:bg-red-500">
                    <AlertTriangle className="w-4 h-4 mr-2" /> Disputes
                  </TabsTrigger>
                  <TabsTrigger value="comments" className="data-[state=active]:bg-blue-500">
                    <MessageSquare className="w-4 h-4 mr-2" /> Comments
                  </TabsTrigger>
                  <TabsTrigger value="payouts" className="data-[state=active]:bg-emerald-500">
                    <Wallet className="w-4 h-4 mr-2" /> Payouts
                  </TabsTrigger>
                  <TabsTrigger value="invoices" className="data-[state=active]:bg-blue-500">
                    <FileText className="w-4 h-4 mr-2" /> Invoices
                  </TabsTrigger>
                  <TabsTrigger value="audit-logs" className="data-[state=active]:bg-orange-500" onClick={fetchAuditLogs}>
                    <Shield className="w-4 h-4 mr-2" /> Audit Logs
                  </TabsTrigger>
                  <TabsTrigger value="background-jobs" className="data-[state=active]:bg-teal-500" onClick={fetchBackgroundJobs}>
                    <Activity className="w-4 h-4 mr-2" /> Background Jobs
                  </TabsTrigger>
                  <TabsTrigger value="error-summary" className="data-[state=active]:bg-rose-500" onClick={fetchErrorSummary}>
                    <AlertCircle className="w-4 h-4 mr-2" /> Errors
                  </TabsTrigger>
                </>
              )}
              </TabsList>
            </div>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Recent Donations */}
                <Card className="bg-card/50 border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-emerald-400" />
                      Recent Donations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {recentActivity.recent_donations?.slice(0, 5).map((donation, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                          <div>
                            <p className="text-foreground font-medium">{donation.donor_name || 'Anonymous'}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(donation.created_at)}</p>
                          </div>
                          <span className="text-emerald-400 font-bold">
                            {formatCurrency(donation.amount)}
                          </span>
                        </div>
                      ))}
                      {(!recentActivity.recent_donations || recentActivity.recent_donations.length === 0) && (
                        <p className="text-muted-foreground text-center py-4">No recent donations</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Users */}
                <Card className="bg-card/50 border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Users className="w-5 h-5 text-emerald-400" />
                      Recent Users
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {recentActivity.recent_users?.slice(0, 5).map((u, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                          <div>
                            <p className="text-foreground font-medium">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                          <Badge className={`${
                            u.kyc_status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                            u.kyc_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-muted text-foreground/80'
                          }`}>
                            {u.kyc_status || 'basic'}
                          </Badge>
                        </div>
                      ))}
                      {(!recentActivity.recent_users || recentActivity.recent_users.length === 0) && (
                        <p className="text-muted-foreground text-center py-4">No recent users</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* KYC Reviews Tab */}
            <TabsContent value="kyc">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground">Pending KYC Verifications</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Review and approve user identity documents
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingKYC.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                      <p className="text-foreground font-medium">All caught up!</p>
                      <p className="text-muted-foreground text-sm">No pending KYC verifications</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingKYC.map((kycUser) => (
                        <div key={kycUser.id} className="p-4 bg-secondary/50 rounded-xl border border-border">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                  <UserCheck className="w-5 h-5 text-emerald-400" />
                                </div>
                                <div>
                                  <h4 className="text-foreground font-medium">{kycUser.name}</h4>
                                  <p className="text-muted-foreground text-sm">{kycUser.email}</p>
                                </div>
                              </div>
                              
                              {/* KYC Documents */}
                              {kycUser.kyc_documents && kycUser.kyc_documents.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  <p className="text-xs text-muted-foreground">Submitted Documents:</p>
                                  {kycUser.kyc_documents.map((doc, i) => (
                                    <div key={i} className="flex items-center gap-2 p-2 bg-card rounded">
                                      <FileText className="w-4 h-4 text-emerald-400" />
                                      <span className="text-foreground text-sm capitalize">{doc.type}</span>
                                      <button 
                                        onClick={() => viewDocument(doc.url)} 
                                        className="text-emerald-400 text-xs hover:underline ml-auto"
                                      >
                                        View Document
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                Submitted: {formatDate(kycUser.kyc_documents?.[0]?.uploaded_at)}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-2 ml-4">
                              <Button
                                size="sm"
                                onClick={() => handleKYCDecision(kycUser.id, 'approved')}
                                disabled={actionLoading[`kyc-${kycUser.id}`]}
                                className="bg-emerald-500 hover:bg-emerald-600"
                              >
                                {actionLoading[`kyc-${kycUser.id}`] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  setKycRejectUserId(kycUser.id);
                                  setKycRejectReason('');
                                  setShowKYCRejectModal(true);
                                }}
                                disabled={actionLoading[`kyc-${kycUser.id}`]}
                              >
                                <XCircle className="w-4 h-4 mr-1" /> Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Campaign Reviews Tab */}
            <TabsContent value="campaigns">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-foreground">Pending Campaign Verifications</CardTitle>
                      <CardDescription className="text-muted-foreground">
                        Review and approve new campaigns
                      </CardDescription>
                    </div>
                    {pendingCampaigns.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={toggleSelectAllPending}
                          className="border-border text-foreground/80"
                        >
                          <CheckSquare className="w-4 h-4 mr-2" />
                          {selectedPendingCampaigns.length === pendingCampaigns.length ? 'Deselect All' : 'Select All'}
                        </Button>
                        {selectedPendingCampaigns.length > 0 && (
                          <>
                            <Button
                              size="sm"
                              onClick={handleBulkApprove}
                              disabled={bulkActionLoading}
                              className="bg-emerald-500 hover:bg-emerald-600"
                            >
                              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                              Approve ({selectedPendingCampaigns.length})
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={handleBulkReject}
                              disabled={bulkActionLoading}
                            >
                              {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                              Reject ({selectedPendingCampaigns.length})
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {pendingCampaigns.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                      <p className="text-foreground font-medium">All caught up!</p>
                      <p className="text-muted-foreground text-sm">No pending campaign verifications</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingCampaigns.map((campaign) => (
                        <div key={campaign.id} className={`p-4 bg-secondary/50 rounded-xl border ${selectedPendingCampaigns.includes(campaign.id) ? 'border-emerald-500' : 'border-border'}`}>
                          <div className="flex items-start gap-4">
                            {/* Checkbox */}
                            <div className="flex items-center pt-2">
                              <Checkbox
                                checked={selectedPendingCampaigns.includes(campaign.id)}
                                onCheckedChange={() => togglePendingCampaignSelection(campaign.id)}
                                className="border-border data-[state=checked]:bg-emerald-500"
                              />
                            </div>
                            
                            {/* Campaign Image */}
                            <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                              <img
                                src={campaign.image || 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=200'}
                                alt={campaign.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            {/* Campaign Info */}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-foreground font-medium mb-1 truncate">{campaign.title}</h4>
                              <p className="text-muted-foreground text-sm line-clamp-2 mb-2 break-words overflow-hidden">
                                {campaign.story?.substring(0, 150)}{campaign.story?.length > 150 ? '...' : ''}
                              </p>
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Target className="w-3 h-3" />
                                  Goal: {formatCurrency(campaign.goal)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  By: {campaign.creator?.name}
                                </span>
                                <Badge className="bg-muted capitalize">{campaign.category}</Badge>
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                onClick={() => window.open(`/campaign/${campaign.id}`, '_blank')}
                                variant="outline"
                                className="border-border"
                              >
                                <Eye className="w-4 h-4 mr-1" /> View
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleCampaignDecision(campaign.id, 'approved')}
                                disabled={actionLoading[`campaign-${campaign.id}`]}
                                className="bg-emerald-500 hover:bg-emerald-600"
                              >
                                {actionLoading[`campaign-${campaign.id}`] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleCampaignDecision(campaign.id, 'rejected', 'Does not meet guidelines')}
                                disabled={actionLoading[`campaign-${campaign.id}`]}
                              >
                                <XCircle className="w-4 h-4 mr-1" /> Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* All Campaigns Tab */}
            <TabsContent value="all-campaigns">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="text-foreground flex items-center gap-2">
                        <Target className="w-5 h-5 text-blue-400" />
                        All Campaigns
                      </CardTitle>
                      <CardDescription className="text-muted-foreground">
                        View and manage all campaigns on the platform
                      </CardDescription>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Search by ID or Title */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search by ID or title..."
                          value={campaignSearchQuery}
                          onChange={(e) => {
                            setCampaignSearchQuery(e.target.value);
                            setCampaignCurrentPage(1);
                          }}
                          className="pl-10 pr-4 py-2 bg-secondary border border-border rounded-lg text-foreground text-sm w-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      {/* Status Filter */}
                      <select
                        value={campaignFilter}
                        onChange={(e) => {
                          setCampaignFilter(e.target.value);
                          setCampaignCurrentPage(1);
                          fetchAllCampaigns(e.target.value);
                        }}
                        className="px-3 py-2 bg-secondary border border-border rounded-lg text-foreground text-sm"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Paused</option>
                        <option value="pending_verification">Pending Review</option>
                        <option value="completed">Completed</option>
                        <option value="rejected">Rejected</option>
                        <option value="deleted">Deleted</option>
                      </select>
                      {/* Sort by Date */}
                      <select
                        value={campaignSortBy}
                        onChange={(e) => {
                          setCampaignSortBy(e.target.value);
                          setCampaignCurrentPage(1);
                        }}
                        className="px-3 py-2 bg-secondary border border-border rounded-lg text-foreground text-sm"
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="raised_high">Highest Raised</option>
                        <option value="raised_low">Lowest Raised</option>
                      </select>
                      <Button 
                        variant="outline" 
                        onClick={() => fetchAllCampaigns(campaignFilter)}
                        disabled={allCampaignsLoading}
                        className="border-border text-foreground/80"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${allCampaignsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {allCampaignsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                      <span className="ml-2 text-muted-foreground">Loading campaigns...</span>
                    </div>
                  ) : (() => {
                    // Filter campaigns by search query
                    const filteredCampaigns = allCampaigns.filter(c => {
                      if (!campaignSearchQuery) return true;
                      const query = campaignSearchQuery.toLowerCase();
                      return (
                        c.id?.toLowerCase().includes(query) ||
                        c.title?.toLowerCase().includes(query) ||
                        c.creator?.name?.toLowerCase().includes(query) ||
                        c.creator?.email?.toLowerCase().includes(query)
                      );
                    });
                    
                    // Sort campaigns
                    const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
                      switch (campaignSortBy) {
                        case 'oldest':
                          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                        case 'raised_high':
                          return (b.current_amount || 0) - (a.current_amount || 0);
                        case 'raised_low':
                          return (a.current_amount || 0) - (b.current_amount || 0);
                        case 'newest':
                        default:
                          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                      }
                    });
                    
                    // Pagination
                    const totalPages = Math.ceil(sortedCampaigns.length / campaignItemsPerPage);
                    const startIndex = (campaignCurrentPage - 1) * campaignItemsPerPage;
                    const paginatedCampaigns = sortedCampaigns.slice(startIndex, startIndex + campaignItemsPerPage);
                    
                    return sortedCampaigns.length === 0 ? (
                      <div className="text-center py-12">
                        <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-foreground font-medium">No Campaigns Found</p>
                        <p className="text-muted-foreground text-sm">
                          {campaignSearchQuery ? `No campaigns matching "${campaignSearchQuery}"` : 
                           campaignFilter === 'all' ? 'No campaigns exist yet' : `No ${campaignFilter.replace('_', ' ')} campaigns`}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                          <span>
                            Showing {startIndex + 1}-{Math.min(startIndex + campaignItemsPerPage, sortedCampaigns.length)} of {sortedCampaigns.length} campaigns
                            {campaignFilter !== 'all' && ` (filtered by: ${campaignFilter.replace('_', ' ')})`}
                          </span>
                          <div className="flex items-center gap-2">
                            <span>Per page:</span>
                            <select
                              value={campaignItemsPerPage}
                              onChange={(e) => {
                                setCampaignItemsPerPage(parseInt(e.target.value));
                                setCampaignCurrentPage(1);
                              }}
                              className="px-2 py-1 bg-secondary border border-border rounded text-foreground text-sm"
                            >
                              <option value="10">10</option>
                              <option value="20">20</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                            </select>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-border">
                                <th className="text-left py-3 px-4 text-foreground/80 font-medium text-sm">ID</th>
                                <th className="text-left py-3 px-4 text-foreground/80 font-medium text-sm">Campaign</th>
                                <th className="text-left py-3 px-4 text-foreground/80 font-medium text-sm">Creator</th>
                                <th className="text-left py-3 px-4 text-foreground/80 font-medium text-sm">Status</th>
                                <th className="text-left py-3 px-4 text-foreground/80 font-medium text-sm">Goal</th>
                                <th className="text-left py-3 px-4 text-foreground/80 font-medium text-sm">Raised</th>
                                <th className="text-left py-3 px-4 text-foreground/80 font-medium text-sm cursor-pointer hover:text-foreground" onClick={() => setCampaignSortBy(campaignSortBy === 'newest' ? 'oldest' : 'newest')}>
                                  Created {campaignSortBy === 'newest' ? '↓' : campaignSortBy === 'oldest' ? '↑' : ''}
                                </th>
                                <th className="text-right py-3 px-4 text-foreground/80 font-medium text-sm">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedCampaigns.map((campaign) => (
                                <tr key={campaign.id} className="border-b border-border/50 hover:bg-secondary/30">
                                  <td className="py-3 px-4">
                                    <span 
                                      className="text-muted-foreground text-xs font-mono cursor-pointer hover:text-blue-400"
                                      onClick={() => {
                                        navigator.clipboard.writeText(campaign.id);
                                        alert('Campaign ID copied!');
                                      }}
                                      title="Click to copy full ID"
                                    >
                                      {campaign.id?.substring(0, 8)}...
                                    </span>
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-3">
                                      <img
                                        src={campaign.image || 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=100'}
                                        alt={campaign.title}
                                        className="w-10 h-10 rounded-lg object-cover"
                                      />
                                      <div className="min-w-0">
                                        <p className="text-foreground font-medium truncate max-w-[200px]">{campaign.title}</p>
                                        <p className="text-muted-foreground text-xs">{campaign.category || 'General'}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    <p className="text-foreground/80 text-sm">{campaign.creator?.name || 'Unknown'}</p>
                                    <p className="text-muted-foreground text-xs">{campaign.creator?.email || ''}</p>
                                  </td>
                                  <td className="py-3 px-4">
                                    <Badge className={`text-xs ${
                                      campaign.status === 'active' ? 'bg-emerald-500' :
                                      campaign.status === 'inactive' ? 'bg-yellow-500 text-black' :
                                      campaign.status === 'pending_verification' ? 'bg-yellow-500 text-black' :
                                      campaign.status === 'completed' ? 'bg-blue-500' :
                                      campaign.status === 'rejected' ? 'bg-orange-500' :
                                      campaign.status === 'deleted' ? 'bg-red-500' : 'bg-slate-500'
                                    }`}>
                                      {campaign.status === 'inactive' ? 'Paused' : campaign.status?.replace('_', ' ')}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className="text-foreground/80">${(campaign.goal || 0).toLocaleString()}</span>
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className="text-emerald-400">${(campaign.current_amount || campaign.raised || 0).toLocaleString()}</span>
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className="text-muted-foreground text-sm">
                                      {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'N/A'}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => window.open(`/campaign/${campaign.id}`, '_blank')}
                                        className="text-foreground/80 hover:text-foreground"
                                        data-testid={`view-campaign-${campaign.id}`}
                                        title="View Campaign"
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                      </Button>
                                      {/* Admin Delete Button - Only for non-deleted campaigns */}
                                      {campaign.status !== 'deleted' && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleAdminDeleteCampaign(campaign.id)}
                                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                          disabled={actionLoading[`delete-${campaign.id}`]}
                                          data-testid={`admin-delete-campaign-${campaign.id}`}
                                          title="Delete Campaign"
                                        >
                                          {actionLoading[`delete-${campaign.id}`] ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : (
                                            <Trash2 className="w-4 h-4" />
                                          )}
                                        </Button>
                                      )}
                                      {/* Admin Restore Button - Only for deleted campaigns */}
                                      {campaign.status === 'deleted' && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleAdminRestoreCampaign(campaign.id)}
                                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                          disabled={actionLoading[`restore-${campaign.id}`]}
                                          data-testid={`admin-restore-campaign-${campaign.id}`}
                                          title="Restore Campaign"
                                        >
                                          {actionLoading[`restore-${campaign.id}`] ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : (
                                            <RefreshCw className="w-4 h-4" />
                                          )}
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-center gap-2 pt-4 border-t border-border">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setCampaignCurrentPage(1)}
                              disabled={campaignCurrentPage === 1}
                              className="border-border text-foreground/80"
                            >
                              First
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setCampaignCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={campaignCurrentPage === 1}
                              className="border-border text-foreground/80"
                            >
                              Previous
                            </Button>
                            <span className="text-muted-foreground text-sm px-4">
                              Page {campaignCurrentPage} of {totalPages}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setCampaignCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={campaignCurrentPage === totalPages}
                              className="border-border text-foreground/80"
                            >
                              Next
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setCampaignCurrentPage(totalPages)}
                              disabled={campaignCurrentPage === totalPages}
                              className="border-border text-foreground/80"
                            >
                              Last
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Success Stories Tab */}
            <TabsContent value="stories">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-400" />
                    Pending Success Stories
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Review and approve user-submitted success stories before they appear publicly
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingStories.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Star className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                      <p>No pending stories to review</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingStories.map((story) => (
                        <div key={story.id} className="bg-background rounded-lg p-4 border border-border">
                          <div className="flex items-start gap-4 mb-3">
                            <img
                              src={story.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(story.user_name)}&background=random`}
                              alt={story.user_name}
                              className="w-10 h-10 rounded-full"
                            />
                            <div className="flex-1">
                              <h4 className="font-semibold text-foreground">{story.user_name}</h4>
                              <p className="text-xs text-gray-400">
                                Campaign: {story.campaign_title} • ${story.amount_raised?.toLocaleString()} raised
                              </p>
                              <p className="text-xs text-gray-500">
                                Submitted: {formatDate(story.created_at)}
                              </p>
                            </div>
                          </div>
                          
                          <h3 className="text-amber-400 font-medium mb-2">{story.title}</h3>
                          <p className="text-gray-300 text-sm mb-3">{story.story}</p>
                          
                          <div className="bg-card rounded p-3 mb-4">
                            <p className="text-xs text-emerald-400 font-medium mb-1">Outcome:</p>
                            <p className="text-gray-300 text-sm">{story.outcome}</p>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleStoryDecision(story.id, 'approved')}
                              disabled={actionLoading[`story-${story.id}`]}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              {actionLoading[`story-${story.id}`] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const reason = prompt('Rejection reason (optional):');
                                handleStoryDecision(story.id, 'rejected', reason || '');
                              }}
                              disabled={actionLoading[`story-${story.id}`]}
                              className="border-red-500 text-red-400 hover:bg-red-500/10"
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteStory(story.id)}
                              disabled={actionLoading[`story-${story.id}`]}
                              className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 ml-auto"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Content Management Tab */}
            <TabsContent value="content">
              <ContentManager />
            </TabsContent>

            {/* Job Applications Tab */}
            <TabsContent value="applications">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-emerald-400" />
                    Job Applications
                  </CardTitle>
                  <CardDescription>Review and manage job applications</CardDescription>
                </CardHeader>
                <CardContent>
                  {jobApplications.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No job applications yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {jobApplications.map((app) => (
                        <div 
                          key={app.id} 
                          className="bg-secondary/50 rounded-xl p-4 border border-border"
                        >
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-foreground">{app.name}</h4>
                                <Badge className={
                                  app.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                  app.status === 'reviewed' ? 'bg-blue-500/20 text-blue-400' :
                                  app.status === 'interviewed' ? 'bg-teal-500/20 text-teal-400' :
                                  app.status === 'hired' ? 'bg-green-500/20 text-green-400' :
                                  'bg-red-500/20 text-red-400'
                                }>
                                  {app.status}
                                </Badge>
                              </div>
                              <p className="text-emerald-400 font-medium mb-1">{app.job_title}</p>
                              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {app.email}
                                </span>
                                {app.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> {app.phone}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> 
                                  {new Date(app.applied_at).toLocaleDateString()}
                                </span>
                              </div>
                              {app.cover_letter && (
                                <p className="mt-2 text-sm text-foreground/80 line-clamp-2">
                                  {app.cover_letter}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                                onClick={() => window.open(`${BACKEND_URL}${app.resume_url}`, '_blank')}
                              >
                                <Download className="w-4 h-4 mr-1" /> Resume
                              </Button>
                              <select
                                value={app.status}
                                onChange={async (e) => {
                                  try {
                                    await apiClient.put(`/careers/applications/${app.id}/status`, {
                                      status: e.target.value
                                    });
                                    fetchAdminData();
                                  } catch (err) {
                                    console.error('Status update error:', err);
                                  }
                                }}
                                className="px-3 py-1.5 rounded-lg bg-muted text-foreground text-sm border border-border focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              >
                                <option value="pending">Pending</option>
                                <option value="reviewed">Reviewed</option>
                                <option value="interviewed">Interviewed</option>
                                <option value="hired">Hired</option>
                                <option value="rejected">Rejected</option>
                              </select>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                                onClick={async () => {
                                  if (window.confirm('Delete this application?')) {
                                    try {
                                      await apiClient.delete(`/careers/applications/${app.id}`);
                                      fetchAdminData();
                                    } catch (err) {
                                      console.error('Delete error:', err);
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Deleted Campaigns Tab */}
            <TabsContent value="deleted">
              <Card className="bg-card border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-foreground flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-red-400" />
                        Deleted Campaigns
                      </CardTitle>
                      <CardDescription className="text-muted-foreground">
                        Restore accidentally deleted campaigns. Restored campaigns will appear in Campaign Reviews for admin approval.
                      </CardDescription>
                    </div>
                    {deletedCampaigns.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={toggleSelectAllDeleted}
                          className="border-border text-foreground/80"
                        >
                          <CheckSquare className="w-4 h-4 mr-2" />
                          {selectedDeletedCampaigns.length === deletedCampaigns.length ? 'Deselect All' : 'Select All'}
                        </Button>
                        {selectedDeletedCampaigns.length > 0 && (
                          <Button
                            size="sm"
                            onClick={handleBulkRestore}
                            disabled={bulkActionLoading}
                            className="bg-emerald-600 hover:bg-emerald-700 text-foreground"
                          >
                            {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                            Restore ({selectedDeletedCampaigns.length})
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {deletedCampaigns.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Trash2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No deleted campaigns</p>
                      <p className="text-sm mt-1">Deleted campaigns will appear here for restoration</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {deletedCampaigns.map((campaign) => (
                        <div key={campaign.id} className={`bg-secondary rounded-lg p-4 border ${selectedDeletedCampaigns.includes(campaign.id) ? 'border-emerald-500' : 'border-red-500/30'}`}>
                          <div className="flex items-start gap-4">
                            {/* Checkbox */}
                            <div className="flex items-center pt-2">
                              <Checkbox
                                checked={selectedDeletedCampaigns.includes(campaign.id)}
                                onCheckedChange={() => toggleDeletedCampaignSelection(campaign.id)}
                                className="border-border data-[state=checked]:bg-emerald-500"
                              />
                            </div>
                            
                            {/* Campaign Image */}
                            <img 
                              src={campaign.image || '/placeholder-campaign.jpg'} 
                              alt={campaign.title}
                              className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                            />
                            
                            {/* Campaign Info */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-foreground truncate">{campaign.title}</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                Creator: {campaign.creator?.name || 'Unknown'} ({campaign.creator?.email || 'N/A'})
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Goal: ${campaign.goal?.toLocaleString() || 0} • Raised: ${campaign.raised?.toLocaleString() || 0}
                              </p>
                              <p className="text-xs text-red-400 mt-1">
                                Deleted: {campaign.deleted_at ? new Date(campaign.deleted_at).toLocaleDateString() : 'Unknown'}
                              </p>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <Button
                                data-testid={`restore-campaign-btn-${campaign.id}`}
                                onClick={() => handleRestoreCampaign(campaign.id)}
                                disabled={actionLoading[`restore-${campaign.id}`]}
                                className="bg-emerald-600 hover:bg-emerald-700 text-foreground"
                              >
                                {actionLoading[`restore-${campaign.id}`] ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                )}
                                Restore
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Enquiries Tab */}
            <TabsContent value="enquiries">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-foreground flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-amber-400" />
                        Customer Enquiries
                        {enquiries.filter(e => e.status === 'new').length > 0 && (
                          <Badge className="ml-2 bg-amber-500/20 text-amber-400 border-amber-500/30">
                            {enquiries.filter(e => e.status === 'new').length} New
                          </Badge>
                        )}
                      </CardTitle>
                      <p className="text-muted-foreground text-sm mt-1">Manage contact forms, support messages, and partnership inquiries. Click on any enquiry to view full details.</p>
                    </div>
                    {/* Bulk Actions */}
                    {selectedEnquiries.length > 0 && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">{selectedEnquiries.length} selected</span>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border-red-500/30"
                          disabled={bulkActionLoading}
                          onClick={async () => {
                            if (!window.confirm(`Are you sure you want to delete ${selectedEnquiries.length} enquiries? This action cannot be undone.`)) return;
                            setBulkActionLoading(true);
                            try {
                              await apiClient.post('/enquiries/bulk-delete', {
                                enquiry_ids: selectedEnquiries
                              });
                              setSelectedEnquiries([]);
                              fetchAdminData();
                            } catch (err) {
                              console.error('Bulk delete error:', err);
                              alert('Failed to delete enquiries');
                            } finally {
                              setBulkActionLoading(false);
                            }
                          }}
                        >
                          {bulkActionLoading ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-1" />
                          )}
                          Delete Selected
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => setSelectedEnquiries([])}
                        >
                          Clear Selection
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {enquiries.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-muted-foreground">No enquiries yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Select All Option */}
                      <div className="flex items-center gap-3 pb-3 border-b border-border">
                        <Checkbox
                          id="select-all-enquiries"
                          checked={selectedEnquiries.length === enquiries.length && enquiries.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedEnquiries(enquiries.map(e => e.id));
                            } else {
                              setSelectedEnquiries([]);
                            }
                          }}
                          className="border-border data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                        />
                        <label htmlFor="select-all-enquiries" className="text-sm text-muted-foreground cursor-pointer">
                          Select All ({enquiries.length})
                        </label>
                      </div>
                      
                      {enquiries.map((enquiry) => (
                        <div 
                          key={enquiry.id} 
                          className={`p-4 rounded-lg border transition-all hover:shadow-lg ${enquiry.status === 'new' ? 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20' : 'bg-secondary/50 border-border hover:bg-secondary'} ${selectedEnquiries.includes(enquiry.id) ? 'ring-2 ring-amber-500/50' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox */}
                            <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedEnquiries.includes(enquiry.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedEnquiries(prev => [...prev, enquiry.id]);
                                  } else {
                                    setSelectedEnquiries(prev => prev.filter(id => id !== enquiry.id));
                                  }
                                }}
                                className="border-border data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                              />
                            </div>
                            
                            {/* Content */}
                            <div 
                              className="flex-1 cursor-pointer"
                              data-testid={`enquiry-item-${enquiry.id}`}
                              onClick={() => {
                                setSelectedEnquiry(enquiry);
                                setShowEnquiryModal(true);
                              }}
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className={
                                  enquiry.type === 'contact' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                                  enquiry.type === 'support' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                                  'bg-green-500/20 text-green-400 border-green-500/30'
                                }>
                                  {enquiry.type?.charAt(0).toUpperCase() + enquiry.type?.slice(1)}
                                </Badge>
                                <Badge className={
                                  enquiry.status === 'new' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                                  enquiry.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                                  enquiry.status === 'resolved' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                  'bg-slate-500/20 text-muted-foreground border-border/30'
                                }>
                                  {enquiry.status?.replace('_', ' ').charAt(0).toUpperCase() + enquiry.status?.slice(1).replace('_', ' ')}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(enquiry.created_at).toLocaleDateString()} {new Date(enquiry.created_at).toLocaleTimeString()}
                                </span>
                              </div>
                              <h4 className="text-foreground font-semibold mb-1">{enquiry.subject}</h4>
                              <p className="text-foreground/80 text-sm mb-2 line-clamp-2">{enquiry.message}</p>
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" /> {enquiry.name || enquiry.contact_name || 'N/A'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {enquiry.email}
                                </span>
                                {enquiry.company_name && (
                                  <span className="flex items-center gap-1">
                                    <Briefcase className="w-3 h-3" /> {enquiry.company_name}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                              {enquiry.status === 'new' && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await apiClient.put(`/enquiries/${enquiry.id}/status?status=in_progress`);
                                      fetchAdminData();
                                    } catch (err) { console.error(err); }
                                  }}
                                >
                                  <Clock className="w-4 h-4 mr-1" /> In Progress
                                </Button>
                              )}
                              {(enquiry.status === 'new' || enquiry.status === 'in_progress') && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await apiClient.put(`/enquiries/${enquiry.id}/status?status=resolved`);
                                      fetchAdminData();
                                    } catch (err) { console.error(err); }
                                  }}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-muted-foreground hover:text-foreground hover:bg-secondary"
                                title="Click to view full details and reply"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEnquiry(enquiry);
                                  setShowEnquiryModal(true);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                title="Delete enquiry"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
                                  try {
                                    await apiClient.delete(`/enquiries/${enquiry.id}`);
                                    fetchAdminData();
                                  } catch (err) { console.error(err); }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Team Pages Tab */}
            <TabsContent value="team-pages">
              <Card className="bg-card/50 border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-foreground">Team Fundraising Pages</CardTitle>
                    <CardDescription>Manage all team fundraising pages across campaigns</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportTeamPages} disabled={teamPagesLoading || teamPages.length === 0}>
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                    <Button variant="outline" onClick={fetchTeamPages} disabled={teamPagesLoading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${teamPagesLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {teamPagesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                    </div>
                  ) : teamPages.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No team pages found</p>
                  ) : (
                    <div className="space-y-4">
                      {teamPages.map((page) => (
                        <div key={page.id} className="p-4 border border-border rounded-lg flex justify-between items-center">
                          <div>
                            <h4 className="font-semibold text-foreground">{page.title}</h4>
                            <p className="text-sm text-muted-foreground">
                              By {page.creator_name} • Goal: ${page.goal} • Raised: ${page.raised || 0}
                            </p>
                            <Badge className={page.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}>
                              {page.status}
                            </Badge>
                          </div>
                          <div className="flex gap-2">
                            {page.status === 'active' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDisableTeamPage(page.id, 'Disabled by admin')}
                                disabled={actionLoading[`team-${page.id}`]}
                              >
                                {actionLoading[`team-${page.id}`] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable'}
                              </Button>
                            )}
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => handleDeleteTeamPage(page.id, page.title)}
                              disabled={actionLoading[`team-delete-${page.id}`]}
                              data-testid={`delete-team-page-${page.id}`}
                            >
                              {actionLoading[`team-delete-${page.id}`] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Activity Logs Tab */}
            <TabsContent value="activity">
              <Card className="bg-card/50 border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-foreground">Activity Logs</CardTitle>
                    <CardDescription>Recent platform activity and user actions</CardDescription>
                  </div>
                  <Button variant="outline" onClick={fetchActivityLogs} disabled={activityLogsLoading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${activityLogsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {activityLogsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                    </div>
                  ) : activityLogs.length === 0 ? (
                    <div className="text-center py-12">
                      <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-foreground font-medium">No Activity Yet</p>
                      <p className="text-muted-foreground text-sm">Platform activity will appear here as users interact with the system</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {activityLogs.map((log, index) => (
                        <div key={`${log.id}-${index}`} className="p-3 bg-secondary/50 border border-border rounded-lg flex items-center gap-3 hover:bg-secondary/70 transition-colors">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            log.type === 'donation' ? 'bg-emerald-500/20' :
                            log.type === 'campaign' ? 'bg-blue-500/20' :
                            log.type === 'user' ? 'bg-purple-500/20' : 'bg-slate-500/20'
                          }`}>
                            {log.type === 'donation' ? <DollarSign className="w-5 h-5 text-emerald-400" /> :
                             log.type === 'campaign' ? <Target className="w-5 h-5 text-blue-400" /> :
                             log.type === 'user' ? <Users className="w-5 h-5 text-purple-400" /> :
                             <Activity className="w-5 h-5 text-muted-foreground" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground font-medium truncate">{log.title}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {log.description}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {log.user_name && <span className="text-muted-foreground">{log.user_name}</span>}
                              {log.user_name && ' • '}
                              {log.created_at ? new Date(log.created_at).toLocaleString() : 'Unknown time'}
                            </p>
                          </div>
                          {log.type === 'donation' && log.amount && (
                            <div className="text-right flex-shrink-0">
                              <span className="text-emerald-400 font-bold">${log.amount.toFixed(2)}</span>
                            </div>
                          )}
                          {log.type === 'campaign' && log.status && (
                            <Badge className={`flex-shrink-0 ${
                              log.status === 'active' ? 'bg-emerald-500' :
                              log.status === 'pending_verification' ? 'bg-yellow-500' :
                              log.status === 'deleted' ? 'bg-red-500' : 'bg-slate-500'
                            }`}>
                              {log.status}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports">
              <div className="space-y-6">
                {/* Quick Platform Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500/20 to-green-500/10 border-emerald-500/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-5 h-5 text-emerald-400" />
                        <span className="text-emerald-400 font-medium text-sm">Total Users</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{stats?.users?.total || 0}</p>
                      <p className="text-xs text-muted-foreground">{stats?.users?.verified || 0} verified</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-500/20 to-indigo-500/10 border-blue-500/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-5 h-5 text-blue-400" />
                        <span className="text-blue-400 font-medium text-sm">Campaigns</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{stats?.campaigns?.total || 0}</p>
                      <p className="text-xs text-muted-foreground">{stats?.campaigns?.active || 0} active • {stats?.campaigns?.pending || 0} pending</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 border-amber-500/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-amber-400" />
                        <span className="text-amber-400 font-medium text-sm">Total Raised</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{formatCurrency(stats?.donations?.amount_raised || 0)}</p>
                      <p className="text-xs text-muted-foreground">{stats?.donations?.total || 0} donations</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/10 border-purple-500/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-5 h-5 text-purple-400" />
                        <span className="text-purple-400 font-medium text-sm">KYC Status</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{stats?.kyc?.approved || 0}</p>
                      <p className="text-xs text-muted-foreground">{stats?.kyc?.pending || 0} pending approval</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Content Moderation Reports */}
                <Card className="bg-card/50 border-border">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-foreground">Content Moderation Reports</CardTitle>
                      <CardDescription>Review and resolve reported users and campaigns</CardDescription>
                    </div>
                    <Button variant="outline" onClick={fetchReports} disabled={reportsLoading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${reportsLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {reportsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                      </div>
                    ) : reports.length === 0 ? (
                      <div className="text-center py-8">
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                        <p className="text-emerald-400 font-medium">All Clear!</p>
                        <p className="text-sm text-muted-foreground mt-1">No pending content reports to review</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {reports.map((report) => (
                          <div key={report.id} className="p-4 border border-border rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Flag className="w-4 h-4 text-red-400" />
                                  <span className="font-semibold text-foreground">{report.reason}</span>
                                  <Badge className={report.status === 'pending' || report.status === 'open' ? 'bg-red-500' : 'bg-gray-500'}>
                                    {report.status}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground mb-1">
                                  Reported: {report.campaign_title || report.user_email}
                                </p>
                                <p className="text-sm text-muted-foreground">{report.details}</p>
                                <p className="text-xs text-muted-foreground mt-2">
                                  By {report.reporter_email} • {new Date(report.created_at).toLocaleString()}
                                </p>
                              </div>
                              {(report.status === 'pending' || report.status === 'open') && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleResolveReport(report.id)}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Announcements Tab */}
            <TabsContent value="announcements">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-card/50 border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground">Create Announcement</CardTitle>
                    <CardDescription>Send platform-wide announcements to all users</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Input
                      placeholder="Announcement title *"
                      value={newAnnouncement.title}
                      onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                      className="bg-secondary"
                    />
                    <Textarea
                      placeholder="Announcement message *"
                      value={newAnnouncement.message}
                      onChange={(e) => setNewAnnouncement(prev => ({ ...prev, message: e.target.value }))}
                      rows={4}
                      className="bg-secondary"
                    />
                    <select
                      className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
                      value={newAnnouncement.type}
                      onChange={(e) => setNewAnnouncement(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="info">Info</option>
                      <option value="warning">Warning</option>
                      <option value="success">Success</option>
                      <option value="urgent">Urgent</option>
                    </select>
                    <Button 
                      onClick={handleCreateAnnouncement}
                      disabled={!newAnnouncement.title || !newAnnouncement.message}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Bell className="w-4 h-4 mr-2" /> Create Announcement
                    </Button>
                  </CardContent>
                </Card>
                <Card className="bg-card/50 border-border">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-foreground">Active Announcements</CardTitle>
                      <CardDescription>Current platform announcements</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchAnnouncements}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {announcementsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                      </div>
                    ) : announcements.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No announcements</p>
                    ) : (
                      <div className="space-y-3">
                        {announcements.map((ann) => (
                          <div key={ann.id} className="p-3 border border-border rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-semibold text-foreground">{ann.title}</h4>
                                <p className="text-sm text-muted-foreground">{ann.message}</p>
                                <Badge className={
                                  ann.type === 'urgent' ? 'bg-red-500' :
                                  ann.type === 'warning' ? 'bg-amber-500' :
                                  ann.type === 'success' ? 'bg-emerald-500' : 'bg-blue-500'
                                }>{ann.type}</Badge>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteAnnouncementClick(ann.id)}>
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <div className="space-y-6">
                <Card className="bg-card/50 border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-cyan-400" />
                      Platform Analytics & Growth Metrics
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      Overview of platform growth and key performance indicators
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      {/* User Growth */}
                      <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/10 rounded-lg p-4 border border-emerald-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="w-5 h-5 text-emerald-400" />
                          <span className="text-emerald-400 font-medium">User Growth</span>
                        </div>
                        <p className="text-3xl font-bold text-foreground">{stats?.users?.total || 0}</p>
                        <p className="text-sm text-muted-foreground">Total registered users</p>
                        <div className="mt-2 text-xs text-emerald-400">
                          +{stats?.users?.verified || 0} verified
                        </div>
                      </div>

                      {/* Campaign Growth */}
                      <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/10 rounded-lg p-4 border border-blue-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="w-5 h-5 text-blue-400" />
                          <span className="text-blue-400 font-medium">Campaign Growth</span>
                        </div>
                        <p className="text-3xl font-bold text-foreground">{stats?.campaigns?.total || 0}</p>
                        <p className="text-sm text-muted-foreground">Total campaigns</p>
                        <div className="mt-2 text-xs text-blue-400">
                          {stats?.campaigns?.active || 0} active
                        </div>
                      </div>

                      {/* Donation Growth */}
                      <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 rounded-lg p-4 border border-amber-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-5 h-5 text-amber-400" />
                          <span className="text-amber-400 font-medium">Donation Growth</span>
                        </div>
                        <p className="text-3xl font-bold text-foreground">{stats?.donations?.count || 0}</p>
                        <p className="text-sm text-muted-foreground">Total donations</p>
                        <div className="mt-2 text-xs text-amber-400">
                          {formatCurrency(stats?.donations?.amount_raised || 0)} raised
                        </div>
                      </div>

                      {/* Revenue Trend */}
                      <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/10 rounded-lg p-4 border border-purple-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <BarChart3 className="w-5 h-5 text-purple-400" />
                          <span className="text-purple-400 font-medium">Revenue Trend</span>
                        </div>
                        <p className="text-3xl font-bold text-foreground">{formatCurrency(stats?.donations?.amount_raised || 0)}</p>
                        <p className="text-sm text-muted-foreground">Total revenue</p>
                        <div className="mt-2 text-xs text-purple-400">
                          {stats?.donations?.tips_collected ? formatCurrency(stats?.donations?.tips_collected) : '$0'} tips
                        </div>
                      </div>
                    </div>

                    {/* Detailed Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-secondary/30 rounded-lg p-4">
                        <h4 className="text-foreground font-medium mb-4">User Statistics</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Total Users</span>
                            <span className="text-foreground font-medium">{stats?.users?.total || 0}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Email Verified</span>
                            <span className="text-emerald-400">{stats?.users?.verified || 0}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">KYC Approved</span>
                            <span className="text-green-400">{stats?.kyc?.approved || 0}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">KYC Pending</span>
                            <span className="text-yellow-400">{stats?.kyc?.pending || 0}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-secondary/30 rounded-lg p-4">
                        <h4 className="text-foreground font-medium mb-4">Campaign Statistics</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Total Campaigns</span>
                            <span className="text-foreground font-medium">{stats?.campaigns?.total || 0}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Active</span>
                            <span className="text-emerald-400">{stats?.campaigns?.active || 0}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Pending Verification</span>
                            <span className="text-amber-400">{stats?.campaigns?.pending || 0}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Completed</span>
                            <span className="text-blue-400">{stats?.campaigns?.completed || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Export Buttons */}
                    <div className="mt-6 flex flex-wrap gap-3 border-t border-border pt-4">
                      <Button
                        variant="outline"
                        className="border-border text-foreground/80"
                        onClick={() => handleExport('users')}
                        disabled={exportLoading.users}
                      >
                        {exportLoading.users ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Export Users CSV
                      </Button>
                      <Button
                        variant="outline"
                        className="border-border text-foreground/80"
                        onClick={() => handleExport('campaigns')}
                        disabled={exportLoading.campaigns}
                      >
                        {exportLoading.campaigns ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Export Campaigns CSV
                      </Button>
                      <Button
                        variant="outline"
                        className="border-border text-foreground/80"
                        onClick={() => handleExport('donations')}
                        disabled={exportLoading.donations}
                      >
                        {exportLoading.donations ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Export Donations CSV
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Referral Rewards Tab — hidden when REFERRALS_ENABLED=false */}
            {REFERRALS_ENABLED && (
              <TabsContent value="referral-rewards">
                <AdminReferralRewardsTable />
              </TabsContent>
            )}

            {/* User Management Tab */}
            <TabsContent value="users-management">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <UserCog className="w-5 h-5 text-indigo-400" />
                    User Management
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Manage user accounts, roles, and access
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Search and Filter */}
                  <div className="flex gap-4 mb-6">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by name, email, or member ID..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="pl-10 bg-secondary border-border text-foreground"
                      />
                    </div>
                    <select
                      value={userRoleFilter}
                      onChange={(e) => setUserRoleFilter(e.target.value)}
                      className="bg-secondary border border-border rounded-md px-3 text-foreground"
                    >
                      <option value="all">All Roles</option>
                      <option value="user">Users</option>
                      <option value="admin">Admins</option>
                      <option value="reviewer">Reviewers</option>
                      <option value="platform_admin">Platform Admins</option>
                    </select>
                    <Button onClick={fetchManagedUsers} className="bg-emerald-500 hover:bg-emerald-600">
                      <Search className="w-4 h-4 mr-2" /> Search
                    </Button>
                  </div>

                  {/* Users List */}
                  {usersLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                    </div>
                  ) : managedUsers.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Click "Search" to load users or enter search criteria</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {managedUsers.map((u) => (
                        <div key={u.id} className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg border border-border">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground font-medium">
                              {u.name?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-foreground font-medium">{u.name}</p>
                                {u.verified && <BadgeCheck className="w-4 h-4 text-emerald-400" />}
                                {u.blocked && <Badge className="bg-red-500 text-xs">Blocked</Badge>}
                              </div>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                              <p className="text-xs text-muted-foreground">ID: {u.member_id || u.id?.slice(0, 8)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={
                              u.role === 'admin' ? 'bg-purple-500' :
                              u.role === 'platform_admin' ? 'bg-red-500' :
                              u.role === 'reviewer' ? 'bg-blue-500' :
                              'bg-slate-500'
                            }>
                              {u.role}
                            </Badge>
                            
                            {/* Role Change */}
                            <select
                              value={u.role}
                              onChange={(e) => handleChangeUserRole(u.id, e.target.value)}
                              disabled={userActionLoading[`role-${u.id}`]}
                              className="bg-muted border border-border rounded px-2 py-1 text-sm text-foreground"
                            >
                              <option value="user">User</option>
                              <option value="reviewer">Reviewer</option>
                              <option value="admin">Admin</option>
                              <option value="platform_admin">Platform Admin</option>
                            </select>
                            
                            {/* Block/Unblock */}
                            {u.blocked ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-500 text-green-400 hover:bg-green-500/10"
                                onClick={() => handleUnblockUser(u.id)}
                                disabled={userActionLoading[`unblock-${u.id}`]}
                              >
                                {userActionLoading[`unblock-${u.id}`] ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                )}
                                Unblock
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
                                onClick={() => handleBlockUser(u.id)}
                                disabled={userActionLoading[`block-${u.id}`]}
                              >
                                {userActionLoading[`block-${u.id}`] ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Ban className="w-3 h-3 mr-1" />
                                )}
                                Block
                              </Button>
                            )}
                            
                            {/* Delete */}
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500 text-red-400 hover:bg-red-500/10"
                              onClick={() => handleDeleteUser(u.id)}
                              disabled={userActionLoading[`delete-${u.id}`]}
                            >
                              {userActionLoading[`delete-${u.id}`] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <UserX className="w-3 h-3 mr-1" />
                              )}
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Disputes Tab */}
            <TabsContent value="disputes">
              <AdminDisputesSection />
            </TabsContent>

            {/* Comments Tab */}
            <TabsContent value="comments">
              <AdminCommentsSection />
            </TabsContent>

            {/* Payouts Tab */}
            <TabsContent value="payouts">
              <AdminPayoutManagement />
            </TabsContent>

            {/* Invoices Tab */}
            <TabsContent value="invoices">
              <AdminInvoicesSection />
            </TabsContent>

            {/* Audit Logs Tab */}
            <TabsContent value="audit-logs">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-foreground flex items-center gap-2">
                        <Shield className="w-5 h-5 text-orange-400" />
                        Audit Logs
                      </CardTitle>
                      <CardDescription className="text-muted-foreground">Track admin actions and system changes</CardDescription>
                    </div>
                    <Button variant="outline" onClick={fetchAuditLogs} disabled={auditLogsLoading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${auditLogsLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {auditLogsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
                    </div>
                  ) : auditLogs.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p>No audit logs found</p>
                      <p className="text-sm">Admin actions will be logged here</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {auditLogs.map((log, idx) => (
                        <div key={log.id || idx} className="bg-secondary/50 rounded-lg p-4 border border-border/50">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-full ${
                                log.action?.includes('delete') ? 'bg-red-500/20 text-red-400' :
                                log.action?.includes('approve') ? 'bg-emerald-500/20 text-emerald-400' :
                                log.action?.includes('reject') ? 'bg-amber-500/20 text-amber-400' :
                                'bg-blue-500/20 text-blue-400'
                              }`}>
                                {log.action?.includes('delete') ? <Trash2 className="w-4 h-4" /> :
                                 log.action?.includes('approve') ? <CheckCircle2 className="w-4 h-4" /> :
                                 log.action?.includes('reject') ? <XCircle className="w-4 h-4" /> :
                                 <Activity className="w-4 h-4" />}
                              </div>
                              <div>
                                <p className="text-foreground font-medium capitalize">{log.action?.replace(/_/g, ' ')}</p>
                                <p className="text-sm text-muted-foreground">
                                  {log.target_type && <span className="text-blue-400">{log.target_type}: </span>}
                                  {log.target_id && <span className="font-mono text-xs">{log.target_id.substring(0, 12)}...</span>}
                                </p>
                                {log.details && (
                                  <p className="text-sm text-muted-foreground mt-1">{JSON.stringify(log.details).substring(0, 100)}...</p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">
                                {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                              </p>
                              {log.admin_email && (
                                <p className="text-xs text-orange-400">{log.admin_email}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Background Jobs Tab (MON-005) */}
            <TabsContent value="background-jobs">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-foreground flex items-center gap-2">
                        <Activity className="w-5 h-5 text-teal-400" />
                        Background Jobs
                      </CardTitle>
                      <CardDescription className="text-muted-foreground">Monitor running and completed background jobs</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        data-testid="run-health-check-btn"
                        onClick={async () => {
                          try {
                            await apiClient.post('/monitoring/jobs/run-health-check');
                            toast.success('Health check started');
                            setTimeout(fetchBackgroundJobs, 600);
                          } catch (err) {
                            toast.error('Failed to start health check');
                          }
                        }}
                      >
                        <Zap className="w-4 h-4 mr-2" /> Run Health Check
                      </Button>
                      <Button variant="outline" onClick={fetchBackgroundJobs} disabled={jobsLoading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${jobsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {jobsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Active Jobs (running + recently completed) */}
                      <div data-testid="active-jobs-panel">
                        <h4 className="text-foreground font-medium mb-3 flex items-center gap-2">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                          Active Jobs ({activeJobs.length})
                        </h4>
                        {activeJobs.length === 0 ? (
                          <div className="text-muted-foreground text-sm p-4 bg-secondary/30 rounded-lg">No jobs currently running. Click "Run Health Check" to start one.</div>
                        ) : (
                          <div className="space-y-2">
                            {activeJobs.map((job, idx) => (
                              <div
                                key={idx}
                                data-testid={`active-job-${idx}`}
                                className={`bg-secondary/50 rounded-lg p-3 border ${job._recent ? 'border-emerald-500/30' : 'border-teal-500/30'}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {job._recent ? (
                                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                    ) : (
                                      <Loader2 className="w-4 h-4 animate-spin text-teal-400" />
                                    )}
                                    <span className="text-foreground font-medium">{job.job_name || job.name}</span>
                                  </div>
                                  <Badge className={job._recent ? 'bg-emerald-500/20 text-emerald-400' : 'bg-teal-500/20 text-teal-400'}>
                                    {job._recent ? `Completed${job.duration_ms ? ` · ${job.duration_ms}ms` : ''}` : 'Running'}
                                  </Badge>
                                </div>
                                {job.started_at && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {job._recent ? 'Ran at' : 'Started'}: {new Date(job.started_at).toLocaleString()}
                                  </p>
                                )}
                                {job.result && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Result: {Object.entries(job.result).slice(0, 3).map(([k, v]) => `${k}=${v}`).join(', ')}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Job History */}
                      <div>
                        <h4 className="text-foreground font-medium mb-3">Job History (Last 50)</h4>
                        {jobHistory.length === 0 ? (
                          <div className="text-muted-foreground text-sm p-4 bg-secondary/30 rounded-lg">No job history found</div>
                        ) : (
                          <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {jobHistory.map((job, idx) => (
                              <div key={job.id || idx} className="bg-secondary/50 rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {job.status === 'completed' ? (
                                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                    ) : job.status === 'failed' ? (
                                      <XCircle className="w-4 h-4 text-red-400" />
                                    ) : (
                                      <Clock className="w-4 h-4 text-amber-400" />
                                    )}
                                    <span className="text-foreground">{job.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {job.duration_ms && (
                                      <span className="text-xs text-muted-foreground">{job.duration_ms}ms</span>
                                    )}
                                    <Badge className={
                                      job.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                                      job.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                      'bg-amber-500/20 text-amber-400'
                                    }>
                                      {job.status}
                                    </Badge>
                                  </div>
                                </div>
                                {job.error && (
                                  <p className="text-xs text-red-400 mt-1">{job.error}</p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                  {job.completed_at ? new Date(job.completed_at).toLocaleString() : new Date(job.started_at).toLocaleString()}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Error Summary Tab (MON-006) */}
            <TabsContent value="error-summary">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-foreground flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-rose-400" />
                        Error Summary
                      </CardTitle>
                      <CardDescription className="text-muted-foreground">Monitor system errors and issues</CardDescription>
                    </div>
                    <Button variant="outline" onClick={fetchErrorSummary} disabled={errorSummaryLoading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${errorSummaryLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {errorSummaryLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-rose-400" />
                    </div>
                  ) : !errorSummary ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p>Click Refresh to load error summary</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Error Stats Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-secondary/50 rounded-lg p-4 border border-rose-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-5 h-5 text-rose-400" />
                            <span className="text-muted-foreground text-sm">5xx Errors</span>
                          </div>
                          <p className="text-2xl font-bold text-rose-400">{errorSummary.total_5xx_errors || 0}</p>
                        </div>
                        <div className="bg-secondary/50 rounded-lg p-4 border border-amber-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-5 h-5 text-amber-400" />
                            <span className="text-muted-foreground text-sm">Period</span>
                          </div>
                          <p className="text-lg font-medium text-foreground">{errorSummary.period || 'Last 24h'}</p>
                        </div>
                        <div className="bg-secondary/50 rounded-lg p-4 border border-border/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Activity className="w-5 h-5 text-muted-foreground" />
                            <span className="text-muted-foreground text-sm">Status</span>
                          </div>
                          <p className={`text-lg font-medium ${errorSummary.total_5xx_errors === 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {errorSummary.total_5xx_errors === 0 ? 'Healthy' : 'Issues Detected'}
                          </p>
                        </div>
                      </div>

                      {/* Error Details */}
                      {errorSummary.recent_errors && errorSummary.recent_errors.length > 0 && (
                        <div>
                          <h4 className="text-foreground font-medium mb-3">Recent Errors</h4>
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {errorSummary.recent_errors.map((error, idx) => (
                              <div key={idx} className="bg-secondary/50 rounded-lg p-3 border border-rose-500/20">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <p className="text-foreground font-medium">{error.error_type || 'Unknown Error'}</p>
                                    <p className="text-sm text-muted-foreground">{error.endpoint || 'N/A'}</p>
                                    {error.message && (
                                      <p className="text-xs text-rose-400 mt-1">{error.message}</p>
                                    )}
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {error.timestamp ? new Date(error.timestamp).toLocaleString() : 'N/A'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tip when no errors */}
                      {(!errorSummary.recent_errors || errorSummary.recent_errors.length === 0) && errorSummary.total_5xx_errors === 0 && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            <p className="text-emerald-400">All systems operating normally. No recent errors detected.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      {/* Donations/Transactions Detail Modal */}
      <Dialog open={showDonationsModal} onOpenChange={setShowDonationsModal}>
        <DialogContent className="max-w-4xl bg-card border-border max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Transaction Details
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Complete list of all donation transactions on the platform
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4 flex-1 overflow-hidden flex flex-col">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-lg p-3 text-center border border-emerald-500/30">
                <p className="text-emerald-400 text-xs font-medium">Total Raised</p>
                <p className="text-xl font-bold text-foreground">{formatCurrency(stats?.donations?.amount_raised || 0)}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3 text-center">
                <p className="text-blue-400 text-xs font-medium">Total Transactions</p>
                <p className="text-xl font-bold text-foreground">{stats?.donations?.total || 0}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3 text-center">
                <p className="text-amber-400 text-xs font-medium">Avg. Donation</p>
                <p className="text-xl font-bold text-foreground">
                  {formatCurrency(stats?.donations?.total > 0 
                    ? (stats?.donations?.amount_raised / stats?.donations?.total) 
                    : 0)}
                </p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3 text-center">
                <p className="text-teal-400 text-xs font-medium">Platform Fee (0%)</p>
                <p className="text-xl font-bold text-foreground">
                  {formatCurrency(0)}
                </p>
              </div>
            </div>

            {/* Transaction List */}
            <div className="bg-secondary/30 rounded-lg flex-1 overflow-hidden flex flex-col">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <h4 className="text-foreground font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  All Transactions ({recentDonations.length})
                </h4>
                <Badge className="bg-emerald-500/20 text-emerald-400">
                  {donationsLoading ? 'Loading...' : `${recentDonations.length} records`}
                </Badge>
              </div>
              
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-secondary/50 text-xs font-medium text-muted-foreground border-b border-border">
                <div className="col-span-1">#</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-2">Donor</div>
                <div className="col-span-3">Campaign</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-2 text-center">Status</div>
              </div>
              
              {/* Transaction Rows */}
              <div className="flex-1 overflow-y-auto">
                {donationsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                  </div>
                ) : recentDonations.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No transactions found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-700">
                    {recentDonations.map((donation, idx) => (
                      <div key={donation.id || idx} className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-secondary/30 transition-colors items-center text-sm">
                        <div className="col-span-1 text-muted-foreground text-xs">{idx + 1}</div>
                        <div className="col-span-2">
                          <p className="text-foreground text-xs">{new Date(donation.created_at).toLocaleDateString()}</p>
                          <p className="text-muted-foreground text-xs">{new Date(donation.created_at).toLocaleTimeString()}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-foreground text-sm truncate">{donation.donor_name || 'Anonymous'}</p>
                          {donation.anonymous && <Badge className="bg-muted text-xs">Anonymous</Badge>}
                        </div>
                        <div className="col-span-3">
                          <p className="text-foreground/80 text-xs truncate">{donation.campaign_title}</p>
                        </div>
                        <div className="col-span-2 text-right">
                          <p className="text-emerald-400 font-semibold">{formatCurrency(donation.amount)}</p>
                        </div>
                        <div className="col-span-2 text-center">
                          <Badge className={
                            donation.status === 'completed' 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : donation.status === 'pending'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-red-500/20 text-red-400'
                          }>
                            {donation.status || 'completed'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between items-center pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Showing {recentDonations.length} most recent transactions
              </p>
              <Button 
                variant="outline" 
                className="border-border text-foreground/80"
                onClick={() => handleExport('donations')}
                disabled={exportLoading.donations}
              >
                {exportLoading.donations ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export All Transactions
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Users Detail Modal */}
      <Dialog open={showUsersModal} onOpenChange={setShowUsersModal}>
        <DialogContent className="max-w-2xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" />
              Users Overview
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Platform user statistics and breakdown
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-emerald-400 text-sm">Total Users</p>
                <p className="text-2xl font-bold text-foreground">{stats?.users?.total || 0}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-blue-400 text-sm">Verified</p>
                <p className="text-2xl font-bold text-foreground">{stats?.users?.verified || 0}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-amber-400 text-sm">KYC Approved</p>
                <p className="text-2xl font-bold text-foreground">{stats?.kyc?.approved || 0}</p>
              </div>
            </div>

            {/* User Breakdown */}
            <div className="bg-secondary/30 rounded-lg p-4">
              <h4 className="text-foreground font-medium mb-3">User Status Breakdown</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    Active Users
                  </span>
                  <span className="text-emerald-400 font-medium">{stats?.users?.active || stats?.users?.total || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    Email Verified
                  </span>
                  <span className="text-blue-400 font-medium">{stats?.users?.verified || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    KYC Pending
                  </span>
                  <span className="text-yellow-400 font-medium">{stats?.kyc?.pending || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    KYC Approved
                  </span>
                  <span className="text-green-400 font-medium">{stats?.kyc?.approved || 0}</span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-end">
              <Button 
                onClick={() => { 
                  setShowUsersModal(false); 
                  // Regular admins use admin route, platform admins use platform-admin route
                  if (user?.role === 'platform_admin' || user?.role === 'super_admin') {
                    navigate('/platform-admin?tab=users');
                  } else {
                    navigate('/admin?tab=kyc');
                  }
                }}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Users className="w-4 h-4 mr-2" />
                Manage All Users
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaigns Detail Modal */}
      <Dialog open={showCampaignsModal} onOpenChange={setShowCampaignsModal}>
        <DialogContent className="max-w-2xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              Campaigns Overview
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Campaign statistics and status breakdown
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-emerald-400 text-sm">Total Campaigns</p>
                <p className="text-2xl font-bold text-foreground">{stats?.campaigns?.total || 0}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-blue-400 text-sm">Active</p>
                <p className="text-2xl font-bold text-foreground">{stats?.campaigns?.active || 0}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-amber-400 text-sm">Pending</p>
                <p className="text-2xl font-bold text-foreground">{stats?.campaigns?.pending || 0}</p>
              </div>
            </div>

            {/* Campaign Breakdown */}
            <div className="bg-secondary/30 rounded-lg p-4">
              <h4 className="text-foreground font-medium mb-3">Campaign Status Breakdown</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    Active Campaigns
                  </span>
                  <span className="text-emerald-400 font-medium">{stats?.campaigns?.active || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    Pending Verification
                  </span>
                  <span className="text-yellow-400 font-medium">{stats?.campaigns?.pending || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    Completed / Goal Met
                  </span>
                  <span className="text-green-400 font-medium">{stats?.campaigns?.completed || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    Deleted
                  </span>
                  <span className="text-red-400 font-medium">{deletedCampaigns.length}</span>
                </div>
              </div>
            </div>

            {/* Fundraising Summary */}
            <div className="bg-secondary/30 rounded-lg p-4">
              <h4 className="text-foreground font-medium mb-3">Fundraising Summary</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center py-3 bg-secondary/50 rounded-lg">
                  <p className="text-muted-foreground text-xs">Total Raised</p>
                  <p className="text-emerald-400 font-bold">{formatCurrency(stats?.donations?.amount_raised || 0)}</p>
                </div>
                <div className="text-center py-3 bg-secondary/50 rounded-lg">
                  <p className="text-muted-foreground text-xs">Total Donations</p>
                  <p className="text-blue-400 font-bold">{stats?.donations?.total || 0}</p>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-end">
              <Button 
                onClick={() => { setShowCampaignsModal(false); navigate('/admin?tab=campaigns'); }}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Target className="w-4 h-4 mr-2" />
                View Campaigns
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Reviews Detail Modal */}
      <Dialog open={showPendingModal} onOpenChange={setShowPendingModal}>
        <DialogContent className="max-w-2xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              Pending Reviews Overview
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Items awaiting admin review and approval
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-yellow-400 text-sm">Total Pending</p>
                <p className="text-2xl font-bold text-foreground">
                  {(stats?.kyc?.pending || 0) + (stats?.campaigns?.pending || 0)}
                </p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-blue-400 text-sm">KYC Requests</p>
                <p className="text-2xl font-bold text-foreground">{stats?.kyc?.pending || 0}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <p className="text-emerald-400 text-sm">Campaign Reviews</p>
                <p className="text-2xl font-bold text-foreground">{stats?.campaigns?.pending || 0}</p>
              </div>
            </div>

            {/* Pending Breakdown */}
            <div className="bg-secondary/30 rounded-lg p-4">
              <h4 className="text-foreground font-medium mb-3">Pending Items Breakdown</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-blue-400" />
                    KYC Verifications
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-400 font-medium">{stats?.kyc?.pending || 0}</span>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-7 text-xs border-border"
                      onClick={() => { setShowPendingModal(false); setActiveTab('kyc'); }}
                    >
                      Review
                    </Button>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <Target className="w-4 h-4 text-emerald-400" />
                    Campaign Approvals
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-400 font-medium">{stats?.campaigns?.pending || 0}</span>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-7 text-xs border-border"
                      onClick={() => { setShowPendingModal(false); setActiveTab('campaigns'); }}
                    >
                      Review
                    </Button>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground/80 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-400" />
                    Success Stories
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-purple-400 font-medium">{pendingStories.length}</span>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-7 text-xs border-border"
                      onClick={() => { setShowPendingModal(false); setActiveTab('stories'); }}
                    >
                      Review
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline"
                className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                onClick={() => { setShowPendingModal(false); setActiveTab('kyc'); }}
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Review KYC
              </Button>
              <Button 
                className="bg-emerald-500 hover:bg-emerald-600"
                onClick={() => { setShowPendingModal(false); setActiveTab('campaigns'); }}
              >
                <Target className="w-4 h-4 mr-2" />
                Review Campaigns
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Enquiry Detail Modal */}
      <Dialog open={showEnquiryModal} onOpenChange={setShowEnquiryModal}>
        <DialogContent data-testid="enquiry-detail-modal" className="max-w-2xl bg-card border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-400" />
              Enquiry Details
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Full details of the customer enquiry
            </DialogDescription>
          </DialogHeader>
          
          {selectedEnquiry && (
            <div className="space-y-6">
              {/* Status and Type */}
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={
                  selectedEnquiry.type === 'contact' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                  selectedEnquiry.type === 'support' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                  'bg-green-500/20 text-green-400 border-green-500/30'
                }>
                  {selectedEnquiry.type?.charAt(0).toUpperCase() + selectedEnquiry.type?.slice(1)}
                </Badge>
                <Badge className={
                  selectedEnquiry.status === 'new' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                  selectedEnquiry.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                  selectedEnquiry.status === 'resolved' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                  'bg-slate-500/20 text-muted-foreground border-border/30'
                }>
                  {selectedEnquiry.status?.replace('_', ' ').charAt(0).toUpperCase() + selectedEnquiry.status?.slice(1).replace('_', ' ')}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {new Date(selectedEnquiry.created_at).toLocaleDateString()} {new Date(selectedEnquiry.created_at).toLocaleTimeString()}
                </span>
              </div>
              
              {/* Subject */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Subject</h3>
                <p className="text-foreground text-lg font-semibold">{selectedEnquiry.subject}</p>
              </div>
              
              {/* Full Message */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Message</h3>
                <div className="bg-secondary/50 rounded-lg p-4 border border-border">
                  <p className="text-foreground whitespace-pre-wrap">{selectedEnquiry.message}</p>
                </div>
              </div>
              
              {/* Contact Information */}
              <div className="bg-secondary/30 rounded-lg p-4 border border-border">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-foreground/80" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Name</p>
                      <p className="text-foreground font-medium">{selectedEnquiry.name || selectedEnquiry.contact_name || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-foreground font-medium">{selectedEnquiry.email}</p>
                    </div>
                  </div>
                  
                  {selectedEnquiry.phone && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                        <Phone className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="text-foreground font-medium">{selectedEnquiry.phone}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedEnquiry.company_name && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Company</p>
                        <p className="text-foreground font-medium">{selectedEnquiry.company_name}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Conversation Thread - Show previous replies */}
              {selectedEnquiry.replies && selectedEnquiry.replies.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Conversation History ({selectedEnquiry.replies.length} replies)</h3>
                  <div className="space-y-3 max-h-[200px] overflow-y-auto">
                    {selectedEnquiry.replies.map((reply, index) => (
                      <div
                        key={reply.id || index}
                        className={`p-3 rounded-lg ${
                          reply.sender_role === 'user'
                            ? 'bg-secondary/50 border-l-2 border-blue-500'
                            : 'bg-emerald-500/10 border-l-2 border-emerald-500'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium ${
                            reply.sender_role === 'user' ? 'text-blue-400' : 'text-emerald-400'
                          }`}>
                            {reply.sender_role === 'user' ? '👤 User' : '🛡️ Admin'}: {reply.sender_name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(reply.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-foreground text-sm whitespace-pre-wrap">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Reply Form */}
              <div className="mt-6 pt-4 border-t border-border">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Send Reply to {selectedEnquiry.name || selectedEnquiry.contact_name || 'User'}
                </h3>
                <div className="space-y-3">
                  <Textarea
                    data-testid="enquiry-reply-textarea"
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply message here..."
                    className="bg-secondary/50 border-border text-foreground min-h-[80px]"
                  />
                  <div className="flex gap-2">
                    <Button
                      data-testid="enquiry-send-reply-btn"
                      className="bg-emerald-500 hover:bg-emerald-600 text-foreground"
                      disabled={!replyMessage.trim() || replyLoading}
                      onClick={async () => {
                        if (!replyMessage.trim()) return;
                        setReplyLoading(true);
                        try {
                          const response = await apiClient.post(`/enquiries/${selectedEnquiry.id}/reply`, {
                            message: replyMessage
                          });
                          // Update local state with new reply
                          setSelectedEnquiry(prev => ({
                            ...prev,
                            replies: [...(prev.replies || []), {
                              id: response.data.reply_id,
                              message: replyMessage,
                              sender_name: 'Admin',
                              sender_role: 'admin',
                              created_at: new Date().toISOString()
                            }]
                          }));
                          setReplyMessage('');
                          // Update the enquiries list locally to reflect the new reply count
                          setEnquiries(prev => prev.map(e => 
                            e.id === selectedEnquiry.id 
                              ? { ...e, replies: [...(e.replies || []), { id: response.data.reply_id, message: replyMessage, sender_name: 'Admin', sender_role: 'admin', created_at: new Date().toISOString() }] }
                              : e
                          ));
                          alert(response.data.user_notified 
                            ? 'Reply sent! User has been notified.' 
                            : 'Reply sent! (User has no account - they won\'t see notification)');
                        } catch (err) {
                          console.error('Failed to send reply:', err);
                          const status = err.response?.status;
                          const detail = err.response?.data?.detail;
                          if (status === 403) {
                            alert('Permission denied: Your account does not have admin access to reply to enquiries.');
                          } else if (status === 404) {
                            alert('Enquiry not found. It may have been deleted.');
                          } else if (status === 429) {
                            alert('Too many requests. Please wait a moment and try again.');
                          } else {
                            alert(detail || `Failed to send reply (${status || 'network error'}). Please try again.`);
                          }
                        } finally {
                          setReplyLoading(false);
                        }
                      }}
                    >
                      {replyLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> Send Reply</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      className="border-border text-foreground/80 hover:bg-secondary"
                      onClick={() => {
                        const mailtoLink = `mailto:${selectedEnquiry.email}?subject=Re: ${encodeURIComponent(selectedEnquiry.subject)}&body=${encodeURIComponent(`Hi ${selectedEnquiry.name || 'there'},\n\nThank you for reaching out to FundTheHope.\n\nRegarding your enquiry:\n"${selectedEnquiry.message}"\n\n`)}`;
                        window.location.href = mailtoLink;
                      }}
                    >
                      <Mail className="w-4 h-4 mr-2" /> Reply via Email Instead
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    💡 If the user has an account with this email, they will receive a notification about your reply.
                  </p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                {/* Copy Email Button */}
                <Button 
                  variant="outline" 
                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(selectedEnquiry.email);
                      alert('Email copied to clipboard!');
                    } catch (err) {
                      // Fallback
                      const textArea = document.createElement('textarea');
                      textArea.value = selectedEnquiry.email;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      alert('Email copied to clipboard!');
                    }
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" /> Copy Email
                </Button>
                
                {/* Status Update Buttons */}
                {selectedEnquiry.status === 'new' && (
                  <Button 
                    variant="outline" 
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
                    onClick={async () => {
                      try {
                        await apiClient.put(`/enquiries/${selectedEnquiry.id}/status?status=in_progress`);
                        fetchAdminData();
                        setShowEnquiryModal(false);
                      } catch (err) { console.error(err); }
                    }}
                  >
                    <Clock className="w-4 h-4 mr-2" /> Mark In Progress
                  </Button>
                )}
                
                {(selectedEnquiry.status === 'new' || selectedEnquiry.status === 'in_progress') && (
                  <Button 
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    onClick={async () => {
                      try {
                        await apiClient.put(`/enquiries/${selectedEnquiry.id}/status?status=resolved`);
                        fetchAdminData();
                        setShowEnquiryModal(false);
                      } catch (err) { console.error(err); }
                    }}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Resolved
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Delete Announcement Confirmation Dialog */}
      <Dialog open={showDeleteAnnouncementConfirm} onOpenChange={setShowDeleteAnnouncementConfirm}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Confirm Delete Announcement
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to delete this announcement? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              className="border-border text-foreground/80 hover:bg-secondary"
              onClick={handleCancelDeleteAnnouncement}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleConfirmDeleteAnnouncement}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* KYC Rejection Reason Modal */}
      <Dialog open={showKYCRejectModal} onOpenChange={setShowKYCRejectModal}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              Reject KYC Verification
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Please provide a reason for rejecting this KYC submission. This will be shared with the user.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm text-foreground/80 mb-2 block">Rejection Reason *</label>
            <Textarea
              value={kycRejectReason}
              onChange={(e) => setKycRejectReason(e.target.value)}
              placeholder="e.g., Documents are blurry, ID expired, Name mismatch..."
              className="bg-background border-border text-foreground min-h-[100px]"
            />
            <div className="mt-2 text-xs text-muted-foreground">
              Common reasons: Documents unclear, ID expired, Name doesn't match, Missing required document
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              className="border-border text-foreground/80 hover:bg-secondary"
              onClick={() => {
                setShowKYCRejectModal(false);
                setKycRejectUserId(null);
                setKycRejectReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              disabled={!kycRejectReason.trim() || actionLoading[`kyc-${kycRejectUserId}`]}
              onClick={async () => {
                if (kycRejectUserId && kycRejectReason.trim()) {
                  await handleKYCDecision(kycRejectUserId, 'rejected', kycRejectReason.trim());
                  setShowKYCRejectModal(false);
                  setKycRejectUserId(null);
                  setKycRejectReason('');
                }
              }}
            >
              {actionLoading[`kyc-${kycRejectUserId}`] ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Reject KYC
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default AdminDashboard;
